/**
 * components/inspection/InspectionChecklistForm.js
 * ХЯНАЛТ ШАЛГАЛТЫН ХУУДАС БӨГЛӨХ — дахин ашиглагдах компонент (I/II/III шат)
 *
 * Дуудагдах газрууд:
 *   • /inspection/xab/:id            — бүрэн хуудсаар
 *   • /inspection/register           — "1-3 шатны шалгалт" товчоор модал дотор
 *   • (мобайл дээр RN хувилбар тусад нь)
 *
 * Props:
 *   inspectionId — бөглөх шалгалтын id (заавал)
 *   onBack       — "Буцах" дарахад (өгөөгүй бол товч харагдахгүй)
 *   onSaved      — амжилттай хадгалсны дараа
 *   onClosed     — дүгнэж хаасны дараа (үр дүнг дамжуулна)
 *
 * Checklist-ийг бүлгээр харуулж, мөр бүрт Тийм/Үгүй тэмдэглэнэ. "Үгүй" гарвал
 * илэрсэн зөрчил + зөрчигдсөн дүрэм + ноцтой зөрчил + арилгах хугацаа + хариуцагч
 * + хаах төлөвийг бөглөнө. Хадгалах (PUT) ба Хаах (оноо/үнэлгээ авто бодно).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Tag,
  Radio,
  Input,
  Select,
  Button,
  message,
  Space,
  Tooltip,
  Row,
  Col,
  Divider,
  Descriptions,
  Alert,
  Spin,
  Empty,
  Modal,
  Popconfirm,
  Switch,
} from "antd";
import moment from "moment";
import {
  xabInspectionApi,
  xabBnWorkplaceApi,
  // ТТ-ийн лавлах — зөрчлийг тоног төхөөрөмж дээр онилоход (2026-10-30)
  xabHrRefApi,
  xabItemAssignmentApi,
} from "src/api/xabApi";
// III шатны комисс — гишүүн бүрд бүлэг оноох (ХЭМАБМТ-06 2.5.1-2.5.3)
import CommissionPanel from "./CommissionPanel";
import FindingPhotos from "src/components/inspection/FindingPhotos";
// Албан шаардлага ҮҮСГЭХ маягт — журмын заавар, хаяглалт,
// «Иймд шаардах нь» загвар (2026-10-05)
import FormalReqCreateModal from "src/components/inspection/FormalReqCreateModal";
// «Бүх нэгжээс» хариуцагч сонгох цонх — цех → хэсэг → ажилтан
import DepartmentTseh from "src/components/departmentTseh";
import DepartmentNegj from "src/components/departmentNegj";
// Б/Д-аар ажилтан хайх — ЗӨРЧЛИЙН БҮРТГЭЛИЙН хуудсан дээрх ЯГ ТЭР хэсэг.
// Маягтын ТЭРГҮҮНД: зөрчил нь ХҮНД холбогддог тул эхлээд хэн болохыг
// баталгаажуулж, дараа нь зөрчлөө тодорхойлно.

// Зөрчлийн маягт — «Зөрчил бүртгэх» хуудастай ЯГ ИЖИЛ харагдац
import InspectionFindingForm from "src/components/inspection/InspectionFindingForm";
// Цонхны савлагааг зөрчлийн маягттай ИЖИЛ болгоход (2026-11-01)
import {
  Card as HzCard,
  SectionTitle as HzSectionTitle,
} from "src/components/hazard/common";
import { getCategories, getSeverities } from "src/api/hazard";
import { uploadHazardPhoto } from "src/api/hazard";
import { resolveViolationMode } from "src/components/inspection/stageAccess";
import { useUserContext } from "src/contexts/userContext";
/* ⚠ Бөглөх хуудсанд тусламж ОГТ байгаагүй (2026-10-01). Жагсаалт дээр нь
   «Хяналт шалгалт бүртгэх» заавар гардаг байсан — бүртгэл нь ЭНД
   хийгддэг тул заавар ч энд байх ёстой. Түлхүүрийг ИЛ дамжуулна: энэ
   компонент /inspection/xab/:id зам дээр ба модал дотор хоёуланд
   дуудагддаг тул замаас уншуулж болохгүй. */
import PageHelp from "src/components/help/PageHelp";


const EVAL_META = {
  good: { label: "Сайн", color: "green" },
  ok: { label: "Хангалттай", color: "gold" },
  bad: { label: "Хангалтгүй", color: "red" },
};
const FIX_META = {
  /* ⚠ Заавал талбар (тайлбар · хугацаа · хариуцагч) дутуу зөрчил.
     Сервер өөрөө тавьдаг — гараар сонгогдохгүй. Арилсан гэж
     тооцогдохгүй тул шалгалт хаагдахыг зогсооно. */
  draft: "Ноорог (дутуу)",
  open: "Нээлттэй",
  in_progress: "Хийгдэж буй",
  // Хариуцагч биелэлтээ илгээсэн — шалгагч баталгаажуулах ёстой. Гараар
  // сонгогдохгүй (зөвхөн хариуцагчийн илгээлтээр орно).
  submitted: "Баталгаажуулах хүлээж буй",
  closed: "Хаагдсан",
};

const FIX_COLOR = {
  draft: { bg: "#F4F5F7", fg: "#6B7280" },
  open: { bg: "#FEF6E7", fg: "#B45309" },
  in_progress: { bg: "#E8F5FE", fg: "#0369A1" },
  submitted: { bg: "#F3EDFF", fg: "#6D28D9" },
  closed: { bg: "#E7F6EC", fg: "#15803D" },
};

/**
 * Зөрчилтэй мөрийн БӨГЛӨГДСӨН талбаруудын товч дүрслэл — «Үзүүлэлтүүд»
 * баганад (зүүн талд), үзүүлэлтийн текстийн доор. Доорх дэлгэрэнгүй
 * мөрийг уншилгүйгээр заалт · ноцтой зөрчил · хариуцагч · хугацаа · төлөв
 * нэг харцаар харагдана; хоосон талбар chip болж гарахгүй.
 */
function FindingChips({ row, seriousList, overdue }) {
  const chips = [];
  const chip = (key, icon, text, bg, fg, title) =>
    chips.push(
      <span
        key={key}
        title={title || text}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          maxWidth: 260,
          padding: "2px 8px",
          borderRadius: 999,
          background: bg,
          color: fg,
          fontSize: 11,
          fontWeight: 600,
          lineHeight: "16px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <i className={`fas ${icon}`} style={{ fontSize: 10, flexShrink: 0 }} />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{text}</span>
      </span>,
    );

  if (row.violated_rule)
    chip("rule", "fa-gavel", row.violated_rule, "#EEF2FF", "#3730A3");
  const sv = row.serious_violation_id
    ? (seriousList || []).find((x) => x.id === row.serious_violation_id)
    : null;
  if (sv)
    chip(
      "sv",
      "fa-exclamation-triangle",
      `Ноцтой · ${sv.code || sv.no || ""}`.trim(),
      "#FEE2E2",
      "#B91C1C",
      sv.name || sv.text,
    );
  if (row.responsible_name)
    chip("resp", "fa-user-check", row.responsible_name, "#E7F6EC", "#166534");
  if (row.due_date)
    chip(
      "due",
      "fa-hourglass-half",
      `${moment(row.due_date).format("YYYY-MM-DD")}${overdue ? " · хэтэрсэн" : ""}`,
      overdue ? "#FEE2E2" : "#FFF7ED",
      overdue ? "#B91C1C" : "#C2410C",
    );
  const st = row.fix_status || "open";
  if (row.responsible_name || row.due_date || row.violated_rule || st !== "open") {
    const c = FIX_COLOR[st] || FIX_COLOR.open;
    chip("st", st === "closed" ? "fa-check-circle" : "fa-circle-notch", FIX_META[st] || st, c.bg, c.fg);
  }
  if (row.fix_photo_url || row.fix_note)
    chip("fix", "fa-reply", "Биелэлт ирсэн", "#F3EDFF", "#6D28D9", row.fix_note);
  if (!chips.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 5 }}>
      {chips}
    </div>
  );
}

/**
 * Хариуцагчийн ИЛГЭЭСЭН БИЕЛЭЛТ + шалгагчийн шийдвэр.
 *   submitted → «Хүлээн авах» (хаана) / «Буцаах» (шалтгаантай → open)
 *   closed    → биелэлтийг түүх болгон харуулна
 * Backend can_verify: шалгагч эсвэл ХАБЭА; хариуцагч өөрөө болохгүй.
 */


/* Онооны АНХДАГЧ хуваарь — журмын 2.6.3.8 (IV шат): мөр бүрийг 0–5
   оноогоор дүгнэнэ, 5-аас бага нь зөрчил.
   ⚠ ХУУДАС БҮР ӨӨР ХУВААРЬТАЙ БАЙЖ БОЛНО: III шат 1–5 (2026-10-07,
     хэрэглэгчийн шийдвэр — журам III шатанд хуваарь заагаагүй, зөвхөн
     «нэгдсэн онооны дагуу» гэсэн 4.1.3/2). Тиймээс доорх нь зөвхөн
     багана/утга байхгүй үеийн нөөц утга; бодит хуваарь нь
     `insp.template.score_min` ба `max_score`-оос гарна. */
const SCORE_MIN_DEFAULT = 0;
const SCORE_MAX_DEFAULT = 5;

// Чимээгүй автомат хадгалалтын давтамж. Нэг мөчлөгт НЭГ PUT явна — 30 секунд
// нь серверт ачаалалгүй, алдагдах ажил хамгийн ихдээ хагас минут.
const AUTOSAVE_MS = 30000;

// ── Албан ёсны маягтын хүснэгтийн загвар (ChecklistBlank-тай нэгэн жигд) ──
const FORM_WRAP = {
  background: "#fff",
  border: "1px solid #E6E9EF",
  borderRadius: 10,
  overflowX: "auto",
  marginBottom: 12,
};
const FORM_TABLE = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 12.5,
  minWidth: 760,
};
const FORM_TH = {
  border: "1px solid #D7DEEA",
  background: "#F2F5F9",
  padding: "6px 8px",
  fontWeight: 700,
  textAlign: "center",
  fontSize: 11,
  textTransform: "uppercase",
};
const FORM_TD = {
  border: "1px solid #E6E9EF",
  padding: "6px 8px",
  verticalAlign: "top",
};
const FORM_GROUP = { ...FORM_TD, background: "#EDF1F6", fontWeight: 700 };

/* ── ГАР УТАС (≤640px): хүснэгтийг мөр бүр нь КАРТ болгон босоо эвхнэ ──
   Толгой нуугдаж, «д/д + үзүүлэлт» нэг мөр, «Тийм / Үгүй» хоёр том товч,
   тэмдэглэл бүтэн өргөн. Inline style-ыг дарах тул !important. Desktop-д
   ямар ч нөлөөгүй — CSS зөвхөн media дотор. */
const ICF_MOBILE_CSS = `
/* ── ҮЙЛДЛИЙН МӨР — дэлгэцэд наалдана ─────────────────────────────────
   Хуудас нь модал дотор ч, бүтэн хуудас дээр ч ажилладаг тул
   position:sticky ашиглав (fixed бол модал дотор буруу байрлана).
   ⚠ Энэ мөр нь template literal дотор тул ХОШУУ хашилт (backtick)
     бичиж БОЛОХГҮЙ — JS мөрийг тасалж, файл бүхэлдээ эвдэрнэ.
   Ард нь цагаан дэвсгэр ЗААВАЛ — эс бөгөөс доогуур нь гүйж буй мөрүүд
   товчны цаагуур харагдана. */
.icf-actionbar {
  position: sticky;
  top: 0;
  z-index: 20;
  background: #fff;
  margin: -16px -16px 8px;   /* эцгийн padding-ийг нөхөж бүтэн өргөн */
  padding: 10px 16px;
  border-bottom: 1px solid #E6E9EF;
  box-shadow: 0 2px 6px rgba(16, 24, 40, .06);
}
@media (max-width: 640px) {
  .icf-actionbar { margin: -4px -4px 6px; padding: 6px 8px; }
}
@media (max-width: 640px) {
  .icf-wrap { overflow-x: hidden !important; border: none !important; background: transparent !important; }
  .icf-table { min-width: 0 !important; display: block; }
  .icf-table thead { display: none; }
  .icf-table tbody { display: block; }
  .icf-table tr { display: flex; flex-wrap: wrap; align-items: stretch;
    background: #fff; border: 1px solid #E6E9EF; border-radius: 10px;
    margin-bottom: 8px; padding: 6px 8px; }
  .icf-table td { border: none !important; padding: 4px 4px !important; }
  .icf-table tr.icf-group { background: #EDF1F6; padding: 8px 10px; }
  .icf-table tr.icf-group td { flex: 1 1 100%; }
  .icf-table td.icf-no { flex: 0 0 30px; font-weight: 700; color: #5B6472; text-align: left !important; }
  .icf-table td.icf-text { flex: 1 1 calc(100% - 34px); }
  .icf-table td.icf-ans { flex: 1 1 45%; border: 1.5px solid #D7DEEA !important; border-radius: 10px;
    margin: 4px 2px 0; padding: 10px 4px !important; font-size: 13px; font-weight: 700; text-align: center !important; }
  .icf-table td.icf-ans .icf-ans-lbl { display: inline; margin-left: 6px; }
  .icf-table td.icf-ans .icf-ans-dot { display: none; }
  .icf-table td.icf-score { flex: 1 1 100%; text-align: left !important; }
  .icf-table td.icf-score .ant-radio-group { display: flex; }
  .icf-table td.icf-score .ant-radio-button-wrapper { flex: 1; text-align: center; height: 36px; line-height: 34px; }
  .icf-table td.icf-note { flex: 1 1 100%; border-top: 1px dashed #E6E9EF !important; margin-top: 4px; }
  /* ⚠ ТӨЛӨВИЙН БАГАНА — УТСАНД ч өөрийн мөрөнд (2026-11-05). Шинэ td-д
     flex дүрэм бичихгүй бол «flex» контейнер дотор агшиж, 150px-ийн
     сонгогч 20px болж харагдана. */
  .icf-table td.icf-fix { flex: 1 1 100%; }
  .icf-table td.icf-fix .ant-select { max-width: 260px; }
  .icf-table td.icf-note textarea { min-height: 28px !important; }
  .icf-table tr.icf-finding { border-left: 3px solid #DC2626; }
  .icf-table tr.icf-finding td { flex: 1 1 100%; }
}
.icf-ans-lbl { display: none; }
/* Ноцтой зөрчлийн сонголт — нэг зүйл 150+ тэмдэгт тул dropdown дотор
   мөр таслан бүтнээр нь харуулна (antd анхдагч: nowrap + ellipsis) */
.icf-sv-dd .ant-select-item-option-content { white-space: normal; line-height: 1.35; font-size: 12px; }
.icf-sv-dd .ant-select-item { padding: 6px 10px; border-bottom: 1px solid #F1F3F6; }
@media (max-width: 640px) {
  .icf-root { padding: 4px !important; }
  .icf-root .ant-space { flex-wrap: wrap; }
}
`;

// Арилгах хугацаа өнгөрсөн, хаагдаагүй зөрчил
const isOverdue = (row) =>
  !!row.due_date &&
  row.fix_status !== "closed" &&
  moment(row.due_date).isBefore(moment(), "day");

export default function InspectionChecklistForm({
  inspectionId,
  onBack,
  onSaved,
  onClosed,
  /**
   * ГАДНААС сонгогдсон цех/хэсэг — хариуцагчийн жагсаалтыг хязгаарлана.
   *
   * ЯАГААД ХЭРЭГТЭЙ ВЭ:
   *   Өмнө нь хариуцагчийн хайлт ЗӨВХӨН `insp.bn_code`-оор явдаг байв.
   *   Гэтэл шалгалт үүсгэхэд цех заагаагүй (жишээ нь ХАБЭА үйлдвэрийн
   *   хэмжээнд эхлүүлсэн) бол `bn_code` хоосон үлдэж, backend нь
   *   ХЭРЭГЛЭГЧИЙН ӨӨРИЙН нэгжээр нөхдөг — өөрөөр хэлбэл шалгаж буй
   *   цехийн биш, шалгагчийн цехийн ажилчид гарч ирдэг байлаа.
   *
   *   Одоо эцэг маягтад (зөрчлийн маягт / шалгалт эхлүүлэх цонх) сонгосон
   *   цех/хэсэг ДАВАМГАЙЛНА.
   */
  bnCode,
  tsehCode,
}) {
  const id = inspectionId;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [insp, setInsp] = useState(null);
  // Албан шаардлага үүсгэх маягт нээлттэй эсэх
  const [frOpen, setFrOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [seriousList, setSeriousList] = useState([]);
  /* Тухайн цех/хэсгийн АЖЛЫН БАЙРНУУД — зөрчлийг ажлын байранд
     хавсаргахад (2026-10-23). Шалгалт татагдсаны ДАРАА л татна:
     цех/хэсгийн код шалгалтаас ирдэг. */
  const [workplaces, setWorkplaces] = useState([]);
  /* Тухайн ЦЕХИЙН тоног төхөөрөмж — зөрчлийг ТТ дээр онилоход
     (2026-10-30). Хоосон байх нь ХЭВИЙН: лавлах нь зөвхөн Баяжуулах
     үйлдвэрт бүрэн. */
  const [devices, setDevices] = useState([]);
  /* ЗӨРЧЛИЙН МОДАЛЬ — аль мөрийнх нээлттэй вэ (`row.id` | null).
     ⚠ ШИЙДВЭР (2026-09-29): шалгах хуудасны мөр дээр ЗӨВХӨН Тийм/Үгүй
       үлдэнэ. «Үгүй» дарахад зөрчлийн бүрэн маягт МОДАЛИАР нээгдэж, бүх
       мэдээллийг тэндээс бүртгэнэ. Өмнө нь 20 гаруй талбар мөрөнд шууд
       байсан тул шалгах хуудас уншигдахааргүй урт болдог байв. */
  const [findingModal, setFindingModal] = useState(null);
  /* Зөрчлийн маягтад хэрэгтэй лавлахууд — «Зөрчил бүртгэх» цонхтой ижил.
     ⚠ Унавал маягт ХЭВИЙН ажиллана, зөвхөн тухайн сонгогч хоосон болно. */
  const [hazCategories, setHazCategories] = useState([]);
  const [hazSeverities, setHazSeverities] = useState([]);
  const { user: meUser, isAdmin: isAdminFn2 } = useUserContext();
  const violationFormMode = React.useMemo(() => {
    const m = resolveViolationMode(
      meUser,
      !!(isAdminFn2 && isAdminFn2()),
    );
    // `all` (sso_owner) нь `getModeConfig`-д байхгүй тул чимээгүй
    // «naryad» руу уначихдаг — энэ маягтад хэзээ ч гарах ёсгүй.
    return m === "all" ? "all_violation" : m;
  }, [meUser, isAdminFn2]);

  /* ⚠ UNMOUNT-ЫН ДАРАА setState ХИЙХГҮЙ (2026-10-15):
       «Can't perform a React state update on an unmounted component»
       гэсэн анхааруулга энэ маягтаас гардаг байв. Шалгагч шалгалтаа
       нээгээд, лавлах/шалгалт татагдаж дуусахаас өмнө хуудсаас гарвал
       (буцах, цонх хаах) хүсэлт нь дараа нь шийдэгдэж, аль хэдийн
       салсан компонент дээр бичих гэж оролддог.
     ⚠ `useState` биш `useRef`: `mounted` нь дахин зурах шаардлагагүй,
       мөн хаалтын (cleanup) дараа уншигдах ёстой. */
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  useEffect(() => {
    getCategories()
      .then((d) => mountedRef.current && setHazCategories(d?.items || d || []))
      .catch(() => mountedRef.current && setHazCategories([]));
    getSeverities()
      .then((d) => mountedRef.current && setHazSeverities(d?.items || d || []))
      .catch(() => mountedRef.current && setHazSeverities([]));
  }, []);

  const onPickPhoto = React.useCallback(async (file) => {
    if (!file) return null;
    try {
      const r = await uploadHazardPhoto(file);
      return { file_url: r.file_url, file_name: r.file_name };
    } catch (e) {
      return null;
    }
  }, []);
  // ── ХАДГАЛААГҮЙ ЗАСВАРЫН ХАМГААЛАЛТ ─────────────────────────────────
  //
  // ⚠ Энэ хуудсанд бөглөсөн бүхэн ЗӨВХӨН React state дээр амьдардаг —
  //   «Хадгалах» дарах хүртэл серверт огт очихгүй. Шалгагч 20-30 үзүүлэлт
  //   үнэлж, хариуцагч хайж, зураг хавсаргаж 10-15 минут зарцуулдаг тул
  //   санамсаргүй таб хаах / F5 / сесс тасрахад бүх ажил алга болно.
  //
  //   State БИШ РЕФ ашиглана. Рендэр бүрд шинэчлэгддэг утга байвал доорх
  //   interval нь хуучин хувилбарыг барьж авах ба хэзээ ч зөв утгыг
  //   харахгүй (stale closure).
  //
  //   ⚠ ЭНГИЙН `dirty` ТУГ БОЛОХГҮЙ. Хадгалалт сүлжээгээр явж байх хооронд
  //     хэрэглэгч үргэлжлүүлэн бичдэг. Туг байвал хариу ирэхэд «цэвэр» гэж
  //     тэмдэглэгдэж, тэр хооронд бичсэн нь мартагдана. Тиймээс ЗАСВАРЫН
  //     ДУГААР — хадгалахын өмнөх дугаарыг тогтоож, амжилттай болмогц
  //     ТҮҮНИЙГ л «хадгалагдсан» гэж тэмдэглэнэ.
  const revRef = useRef(0);
  const savedRevRef = useRef(0);
  const isDirty = () => revRef.current !== savedRevRef.current;
  // Автомат ба гар хадгалалт ДАВХЦАХААС сэргийлнэ
  const savingRef = useRef(false);
  // Interval нь ҮҮСЭХ агшны `doSave`-ыг барьдаг тул хамгийн сүүлийн
  // хувилбарыг реф дундуур дуудна — эс бөгөөс ХУУЧИН `items` хадгалагдана.
  const saveRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      // Зөрчлийн төрөл / шийтгэлийн лавлахыг энд татахаа болив — зөрчлийн
      // бүрэн маягт МОДАЛИАР нээгддэг (2026-09-29) тул тэр нь өөрөө татна.
      const [data, serious] = await Promise.all([
        xabInspectionApi.get(id),
        xabInspectionApi.getSeriousViolations().catch(() => []),
      ]);
      /* ⚠ Хуудсаас гарсан бол бичихгүй — реф нь хэвээр үлдэх ч
         төлөв нь салсан компонентынх. */
      if (!mountedRef.current) return;
      setInsp(data);
      setItems((data.items || []).map((it) => ({ ...it })));
      setSeriousList(serious || []);
      /* ── АЖЛЫН БАЙРНУУД (2026-10-23) ──────────────────────────────
         Шалгалт татагдсаны ДАРАА, түүний цех/хэсгээр ШҮҮЖ татна.
         ⚠ Шүүлтгүй татвал бүх цехийн 123 ажлын байр гарч, шалгагч өөр
           цехийн байрыг сонгох боломжтой болно.
         ⚠ Алдаа гарвал ХООСОН жагсаалт — зөрчил бүртгэх нь ажлын
           байрнаас хамаарах ёсгүй (ажлын байр заавал биш). */
      try {
        const wp = await xabBnWorkplaceApi.list({
          tseh_code: data.tseh_code || undefined,
          bn_code: data.bn_code || undefined,
          /* ⚠ ГРУПП (2026-10-29) — шалгалт группээр хийгдсэн бол ажлын
             байрыг ч группээр хумина. Сервер нь группгүй (NULL) мөрийг
             хасахгүй: тэд «бүх хэсэгт» хамаарна. */
          grp_code: data.grp_code || undefined,
        });
        if (mountedRef.current) setWorkplaces(wp?.items || []);
      } catch (_we) {
        if (mountedRef.current) setWorkplaces([]);
      }
      /* ── ТОНОГ ТӨХӨӨРӨМЖ (2026-10-30) ─────────────────────────────
         Зөрчлийг онилох 3-р тэнхлэг (хэрэглэгч: «1. ажлын байр,
         2. Тоног төхөөрөмж, 3. Хувь хүн»).
         ⚠ ЦЕХЭЭР ШҮҮНЭ: 591 ТТ-г бүгдийг татвал шалгагч өөр цехийн
           төхөөрөмжийг сонгож болно. `unit_code` нь угтвараар
           тааруулдаг (сервер `LIKE 'код%'`).
         ⚠ ЛАВЛАХ НЬ ТЭГШ БИШ: 595 ТТ-ийн 589 нь Баяжуулах үйлдвэрт
           (цех 11), бусад 55 цехэд ТТ АЛГА
           ([[reference_inspection_workplace]]). Тиймээс хоосон байх нь
           ХЭВИЙН — сонгогч нь түүнийг хэлнэ, зөрчил бүртгэхийг
           зогсоохгүй. */
      try {
        const dv = await xabHrRefApi.devices({
          unit_code: data.tseh_code || undefined,
          limit: 500,
        });
        if (mountedRef.current) setDevices(dv?.items || []);
      } catch (_de) {
        if (mountedRef.current) setDevices([]);
      }
      revRef.current = 0;       // серверийнхтэй яг тэнцүү боллоо
      savedRevRef.current = 0;
    } catch (e) {
      if (mountedRef.current) message.error("Шалгалт татахад алдаа гарлаа");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  /** ЗӨВХӨН хавсралтын жагсаалтыг сэргээнэ — мөрүүдэд ХҮРЭХГҮЙ.
   *
   *  ⚠ ЯАГААД ТУСДАА ВЭ (2026-08-17): өмнө нь зураг хавсаргамагц `load()`
   *    бүтнээр ажиллаж, `setItems(...)` нь ХАДГАЛААГҮЙ бүх засварыг
   *    серверийн хувилбараар дардаг байв. Шалгагч 20-30 үзүүлэлт бөглөөд
   *    зураг хавсаргахад үнэлгээ, хариуцагч, тайлбар, хугацаа бүгд арилж,
   *    хуудас дахин ачаалагдсан мэт харагддаг байсан.
   *
   *    Зурагт хэрэгтэй нь ЗӨВХӨН `insp.files` тул түүнийг л шинэчилнэ.
   *    `items`-д хүрэхгүй тул бөглөж байсан зүйл бүрэн хэвээр үлдэнэ. */
  const reloadFiles = async () => {
    try {
      const data = await xabInspectionApi.get(id);
      if (!mountedRef.current) return;
      setInsp((prev) => (prev ? { ...prev, files: data.files || [] } : data));
    } catch (e) {
      // Хавсралтын жагсаалт хоцрох нь бөглөсөн зүйл алдагдахаас хамаагүй дээр
      if (mountedRef.current) message.error("Хавсралтыг сэргээж чадсангүй");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // «Түгжигдсэн» = хаагдсан ЭСВЭЛ энэ хэрэглэгчид засах эрх байхгүй
  // (backend `can_modify`: оруулсан/шалгасан хүн нээлттэй үед, ХАБЭА үргэлж).
  // Эрхгүй хүн хуудсыг харна, гэхдээ бүх талбар идэвхгүй — эс бөгөөс
  // бөглөөд хадгалах үед 403 авч, засвар нь алдагдана.
  const noRight = !!insp && insp.can_modify === false;
  const closed = insp?.status === "closed" || noRight;

  /* ── III ШАТНЫ КОМИСС (ХЭМАБМТ-06 2.5.1-2.5.3) ────────────────────────
     Комисс бүрдсэн бол гишүүн ЗӨВХӨН өөрт оноогдсон бүлгийг харж, засна
     (`own_groups_only`); комиссын УДИРДАГЧ (chair) ба ХАБЭА/sso_owner
     бүгдийг. Бүлэггүй мөр (group_id хоосон) хэнд ч нээлттэй — хуучин
     загварт бүлэг байхгүй байж болно. */
  const commission = insp?.commission || null;
  const restricted = !!commission?.restricted;
  /* ⚠ БҮЛГИЙН хязгаарлалт нь `restricted`-ЭЭС ӨӨР ТУГ (2026-10-28).
     `restricted` нь толгойн талбар ба «Дүгнэж хаах» товчийг удирддаг
     бөгөөд шалгалтыг ӨӨРӨӨ үүсгэсэн хүнийг чөлөөлдөг. Тэр нь бүлгийн
     харагдацад ТААРАХГҮЙ: ролиороо ердөө `member` боловч шалгалтыг
     үүсгэсэн хүн өөрт оноогоогүй БҮХ бүлгийг харж байв (бодит жишээ:
     INS-202610-0774 · ТН 79034 — 3 бүлэг оногдсон, 10 бүлэг нээлттэй).
     Сервер `own_groups_only`-г КОМИССЫН РОЛЬД тулгуурлан буцаана. */
  const ownGroupsOnly = !!commission?.own_groups_only;
  const myGroupIds = useMemo(
    () => new Set((commission?.my_groups || []).map(Number)),
    [commission],
  );
  /* ── ГИШҮҮН ТУС БҮРИЙН БҮРТГЭЛ — III ШАТ (2026-11-05) ────────────────
     Хэрэглэгч: «3 р шатны хяналтын хуудасыг хэд хэдэн хүн бөглөдөг.
     гэтэл нэг хуудас дээр өөрийн бүртгэлийг шинэ бүртгэх биш нөгөө
     хүний оруулсанг засахаар орж болж байна.»

     Сервер `per_member` тугтай үед мөр тутамд `mine` (ХЭНИЙХ) ба
     үзүүлэлт тутамд `score_avg` · `score_n` · `score_tns` ирнэ.
     ⚠ `mine`-ыг СЕРВЕР тооцдог: ТН-ийн тэргүүн тэг, төрлийн зөрүү
       (текст vs тоо) нь клиент талд чимээгүй буруу дүн гаргадаг
       ([[reference_training_integration]]-ийн ТН-ийн зангуу). */
  const perMember = !!insp?.per_member;
  /* Бүлэгт ОНООГДСОН гишүүдийн тоо — «3/5 үнэлсэн» гэж харуулахад.
     ⚠ Бүлэггүй мөрд (group_id хоосон) комиссын БҮХ гишүүнийг тооцохгүй:
       тэр мөр хэнд ч нээлттэй тул «хэд үнэлэх ёстой» нь тодорхойгүй. */
  const groupMemberCount = useMemo(() => {
    const out = new Map();
    (commission?.members || []).forEach((m) => {
      (m.group_ids || []).forEach((g) => {
        const k = Number(g);
        out.set(k, (out.get(k) || 0) + 1);
      });
    });
    return out;
  }, [commission]);
  /* ТН → нэр (хэн үнэлсэн/үнэлээгүйг нэрээр хэлэхэд) */
  const memberNameByTn = useMemo(() => {
    const out = new Map();
    (commission?.members || []).forEach((m) => {
      if (m.tn) out.set(String(m.tn), m.name || String(m.tn));
    });
    return out;
  }, [commission]);

  const rowLocked = (row) =>
    closed ||
    (ownGroupsOnly &&
      row.group_id != null &&
      !myGroupIds.has(Number(row.group_id))) ||
    /* ⚠ БУСДЫН БҮРТГЭЛ — ЗӨВХӨН ХАРАХ. Сервер ч хамгаална (ирсэн `id`
       нь өөр гишүүний бол үл тоомсорлож өөрийн мөрийг үүсгэдэг), гэхдээ
       UI нь ЗАСАГДАХГҮЙГ ИЛТ хэлэх ёстой — эс бөгөөс хэрэглэгч бичээд
       хадгалахад өөр мөрөнд орж, «засвар алга болсон» гэж ойлгоно. */
    (perMember && row.mine === false);
  // Гишүүнд анхдагчаар ЗӨВХӨН өөрийн бүлгүүд харагдана (хэрэгцээт хуудас нь
  // идэвхтэй); «Бусдын бүлгийг харах» асаавал бусад нь зөвхөн харах горимоор.
  const [showOthers, setShowOthers] = useState(false);
  const groupVisible = (grp) => {
    if (!ownGroupsOnly || showOthers) return true;
    const gid = grp.rows[0]?.group_id;
    return gid == null || myGroupIds.has(Number(gid));
  };
  /* Миний хариуцах мөрийн тоо — баннерт харуулна. */
  const myItemCount = useMemo(
    () =>
      myGroupIds.size
        ? items.filter((it) => myGroupIds.has(Number(it.group_id))).length
        : 0,
    [items, myGroupIds],
  );

  // Бүлгийн хариуцагч (толгойд харуулна)
  const groupOwner = useMemo(() => {
    const m = {};
    (commission?.members || []).forEach((mem) =>
      (mem.group_ids || []).forEach((gid) => {
        m[gid] = m[gid] ? m[gid] + ", " + (mem.name || mem.tn) : mem.name || mem.tn;
      }),
    );
    return m;
  }, [commission]);

  // Үнэлгээний горим.
  // ⚠ IV шатанд мэргэжилтэн тус бүр өөрийн хуудастай бөгөөд ЗАРИМ нь 0-5
  // оноогоор (Манлайлал, ЭА, ГТУХАБ, Хөдөлгөөний АБ, ЦХАБ), ЗАРИМ нь
  // Тийм/Үгүй-гээр үнэлдэг. Тиймээс эхлээд ХУУДСЫГ, эс бөгөөс шатыг үзнэ.
  /* ⚠ УДИРДАМЖ ХАМГИЙН ДЭЭД ЭРХТЭЙ (2026-10-08, хэрэглэгчийн шийдвэр).
     Зорилтот ба ээлжит бус хяналтад журам маягт ч, оноо ч заагаагүй тул
     ШАТНЫ `eval_mode` нь `none` байдаг. Үнэлгээг УДИРДАМЖ дээр сонгоно
     («0-5 оноо» / «Тийм-Үгүй» / «Үнэлгээгүй»). Backend нь үүнийг аль
     хэдийн дагадаг (`_directive_eval_mode`, 2026-10-07) атал маягт
     дагахгүй байсан тул «0-5 оноо» гэж сонгосон удирдамжийн дагуу явж
     буй шалгалт дээр Тийм/Үгүй товч гарч, оноо тавих боломжгүй байв.
     ⚠ `none` нь «санал байхгүй» — ДАРЖ БИЧИХГҮЙ, маягт/шат руу доош
       унана (backend-тэй ЯГ ИЖИЛ). `none`-ийг «Тийм/Үгүй» гэж үзвэл
       үнэлгээгүй байх ёстой шалгалт хүчээр дүгнэгдэнэ. */
  const directiveMode = String(insp?.directive?.eval_mode || "")
    .trim()
    .toLowerCase();
  const scoreMode =
    directiveMode === "score"
      ? true
      : directiveMode === "yesno"
        ? false
        : insp?.template?.score_mode === "score" ||
          (!insp?.template?.score_mode &&
            insp?.stage_eval_mode === "percent");

  /* Онооны хуваарь — ХУУДСЫН ЛАВЛАХААС (III 1–5 · IV 0–5).
     ⚠ `score_min` нь 0 байж болох тул `|| SCORE_MIN_DEFAULT` гэж бичиж
        БОЛОХГҮЙ — 0 нь falsy тул чимээгүй алдагдана. */
  const scoreMin =
    insp?.template?.score_min != null
      ? Number(insp.template.score_min)
      : SCORE_MIN_DEFAULT;
  const scoreMax = Number(insp?.template?.max_score) || SCORE_MAX_DEFAULT;
  const scoreOptions = useMemo(() => {
    const out = [];
    for (let n = scoreMin; n <= scoreMax; n += 1) out.push(n);
    return out;
  }, [scoreMin, scoreMax]);

  // Мөр зөрчилтэй юу — оноон горимд ДЭЭД ОНООноос бага, эсвэл "Үгүй".
  const isFinding = (row) =>
    scoreMode ? row.score != null && row.score < scoreMax : row.answer === "no";

  const setItem = (rowId, patch) => {
    revRef.current += 1;
    setItems((prev) =>
      prev.map((it) => (it.id === rowId ? { ...it, ...patch } : it))
    );
  };

  /** Зөрчил болмогц «зөрчигдсөн дүрэм»-ийг лавлахаас урьдчилан бөглөнө.
   *  Шалгах хуудсанд заалт (rule_ref) бичигдсэн байдаг тул шалгагч гараар
   *  хуулах шаардлагагүй. Гараар засах боломж хэвээр. */
  /** АЖЛЫН БАЙРЫГ анхдагчаар шалгалтынхаар бөглөнө (2026-10-26).
   *
   * ⚠ ЯАГААД: сонгогч 2026-10-23-наас production дээр байсан атлаа
   *   10-23-аас хойшхи 52 зөрчлийн **0-д** бөглөгдсөн. Эвдрээгүй —
   *   хэрэглэгч тэр талбар хүртэл хүрдэггүй байв. Шалгалт өөрөө
   *   тодорхой ажлын байранд хийгдэж байгаа бол тэндээс илэрсэн
   *   зөрчил мөн тэр байрных байх нь хэвийн.
   * ⚠ Зөвхөн ХООСОН үед — гараар сонгосныг дарж бичихгүй.
   * ⚠ `insp.workplace_id` нь серверээс 339b4e2-оос хойш ирдэг
   *   (`_workplace_info`). Ирээгүй бол юу ч хийхгүй.
   */
  /* Заалтын эхийг tooltip-д ХУМЖИНА.
   *
   * ⚠ ХЭМЖСЭН (2026-10-27): лавлахын заалтууд 1300-1900 тэмдэгт хүрдэг
   *   (жш: reg 42-ын 5.4 нь 1909). Бүтнээр tooltip-д тавибал дэлгэцийг
   *   бүхэлд нь дарж, доорх үзүүлэлтүүд уншигдахгүй болно.
   * ⚠ Скан журмын OCR нь мөрийн СҮҮЛД хүснэгт/гарын үсгийн блокийг
   *   залгадаг (195 шинэ заалтын 4 нь ийм) — эхний хэсэг нь жинхэнэ
   *   заалт тул хумих нь агуулгыг ч цэвэршүүлнэ.
   */
  const clauseBrief = (t) => {
    const s = String(t || "").replace(/\s+/g, " ").trim();
    return s.length > 420 ? `${s.slice(0, 419)}…` : s;
  };

  const prefillWorkplace = (row, patch) => {
    if (!row.workplace_id && insp?.workplace_id) {
      patch.workplace_id = insp.workplace_id;
    }
    return patch;
  };

  const prefillRule = (row, patch) => {
    if (row.rule_ref && !row.violated_rule) patch.violated_rule = row.rule_ref;
    /* ЗААЛТЫН БҮТЭЦТЭЙ ХОЛБООС — үзүүлэлт → зөрчил → албан шаардлага.
     *
     * Шалгах хуудасны үзүүлэлт нь журмын лавлахын заалттай холбогдсон
     * бол (`row.clause`, сервер 94b8d4c-оос хойш буцаана) зөрчилд
     * ТҮҮНИЙГ өвлүүлнэ. Ингэснээр албан шаардлагын `clause_no` /
     * `clause_text` / `regulation_name` сервер талдаа өөрөө
     * бөглөгдөнө — өмнө нь шалгагч ижил эшлэлийг 3 газар гараар
     * бичдэг байсан.
     *
     * ⚠ ДАРЖ БИЧИХГҮЙ: шалгагч заалтаа аль хэдийн сонгосон бол
     *   (`violation_clause_id`) хөндөхгүй — тэр нь ТУХАЙН зөрчилд
     *   тохирох заалт, үзүүлэлтийн ерөнхий эшлэлээс чухал.
     * ⚠ Бичвэрийг ДЕНОРМАЛААР хуулна (600 тэмдэгт — багананы хэмжээ):
     *   журам хожим засагдсан ч зөрчил бүртгэгдсэн үеийн үг
     *   хэллэгээрээ үлдэх ёстой (эрх зүйн баримт).
     */
    const cl = row.clause;
    if (cl && cl.clause_id && !row.violation_clause_id) {
      patch.violation_clause_id = cl.clause_id;
      const txt = [cl.clause_no, (cl.clause_text || "").replace(/\s+/g, " ")]
        .filter(Boolean)
        .join(" · ");
      if (txt) patch.violation_clause_text = txt.slice(0, 600);
    }
    return patch;
  };

  /** Ноцтой зөрчил сонгоход «зөрчигдсөн дүрэм»-д ЛАВЛАХЫН КОДЫГ тавина.
   *
   *  Хавсралт №1-ийн 17 зүйл нь журмын лавлахад ХЭМАБМТ-06-Х1.N заалт болж
   *  орсон тул зөрчигдсөн дүрмийг гараар бичихгүй, кодоор нь заана. Ингэснээр
   *  зөрчил → заалт хамаарал бодитоор хайгдана (өмнө нь чөлөөт бичвэр байсан).
   *
   *  ⚠ Шалгах хуудсанд өөрийн `rule_ref` бичигдсэн бол ТҮҮНИЙГ ДАРАХГҮЙ —
   *    тэр нь тухайн үзүүлэлтийн ТУСГАЙ заалт. Урьд нь «violated_rule
   *    бөглөгдсөн бол юу ч хийхгүй» байсан тул «Үгүй» гэмэгц rule_ref
   *    орж ирээд, дараа нь ноцтой зөрчил сонгоход хавсралтын заалт ХЭЗЭЭ Ч
   *    орж ирдэггүй байв (2026-09-22). Одоо «; »-ээр тусгаарлан НЭМНЭ,
   *    ноцтой зөрчлийг солих/арилгахад өмнөх кодыг нь хасна — гараар
   *    бичсэн бусад хэсэг хэвээр. */
  const onAnswer = (row, val) => {
    /* ⚠ ДАХИН ДАРВАЛ ЦЭВЭРЛЭНЭ (2026-10-15): өмнө нь ижил утгаа дахин
       тавьдаг тул санамсаргүй дарсныг буцаах зам байхгүй байв. */
    if (row.answer === val) {
      clearRow(row);
      return;
    }
    const patch = { answer: val };
    if (val === "no" && !row.fix_status) {
      patch.fix_status = "open";
      prefillRule(row, patch);
      prefillWorkplace(row, patch);
    }
    if (val === "yes") {
      // Тийм болгоход зөрчлийн талбаруудыг цэвэрлэхгүй (түүх хадгална), гэхдээ
      // fix_status-ыг арилгана — зөрчилгүй мөр
      patch.fix_status = row.finding_text ? row.fix_status : null;
    }
    setItem(row.id, patch);
    // Зөрчил илэрсэн бол бүртгэлийн маягтыг ШУУД нээнэ — нэмэлт товч
    // хайх шаардлагагүй. «Тийм» бол нээлттэй байсныг хаана.
    if (!rowLocked(row)) setFindingModal(val === "no" ? row.id : null);
  };

  /* ══════════ БҮГДИЙГ «ТИЙМ» (олон мөртэй хуудсанд) ══════════
     Хэрэглэгч: «чек лист их олон байгаа үед нэг бүрчлэн чек хийх
     төвөгтэй … бүгдийг чеклэх сонголт, тэгээд асуудалтай газраа «Үгүй»
     гээд зөрчлөө оруулъя».

     ⚠ ЗӨВХӨН БӨГЛӨӨГҮЙ МӨРИЙГ хөндөнө. Аль хэдийн «Үгүй» гэж тэмдэглэсэн
       мөрийг «Тийм» болговол тэнд БҮРТГЭСЭН ЗӨРЧИЛ (гаргагч · шийтгэл ·
       арга хэмжээ · хугацаа) нь «зөрчилгүй» мөрөнд үлдэж, тайлан, албан
       шаардлага ХУДАЛ болно. Бөглөсөн оноог ч дарахгүй.

     ⚠ `rowLocked` мөрийг УГ ХАСНА: комиссын гишүүн зөвхөн өөрийн бүлгийг
       бөглөх эрхтэй (журам 2.5.3) — бусдын бүлгийг нэг товчоор бөглөчихвөл
       гишүүн тус бүрийн хариуцлага замхарна.

     ⚠ `setItems`-ыг НЭГ УДАА дуудна. Мөр тутамд `setItem` дуудвал 100+
       рендэр болж хуудас мэдэгдэхүйц гацна.

     ⚠ `useMemo` ХЭРЭГЛЭХГҮЙ: энэ хэсэг компонентийн эрт return-ээс хойш
       байрладаг тул hook нь нөхцөлт болж build унана. Шүүлт нь хямд. */
  const blankRows = (items || []).filter(
    (it) => !rowLocked(it) && (scoreMode ? it.score == null : !it.answer),
  );
  /* ⚠ ШОШГЫГ ЭНД БОДНО, JSX ДОТОР ternary БИЧИХГҮЙ.
       `eslint-plugin-react-hooks`-ийн код-замын шинжилгээ нь нөхцөлт
       рендэрийн дотор ДАХИН гурвалсан нөхцөл орвол эндүүрч, ФАЙЛЫН БҮХ
       hook-ийг «нөхцөлт» гэж 11 алдаа өгөөд build-ыг унагаасан
       (2026-10-01-нд eslint-ээр хуваан тогтоов: ternary нь гэм, template
       literal нь гэмгүй). Тиймээс JSX дотор зөвхөн хувьсагч тавина. */
  const bulkLabel = scoreMode
    ? `Бүгдийг ${scoreMax} оноо`
    : "Бүгдийг Тийм";
  const bulkTitle = scoreMode
    ? `Бөглөөгүй ${blankRows.length} мөрийг ${scoreMax} оноо болгох уу?`
    : `Бөглөөгүй ${blankRows.length} мөрийг «Тийм» болгох уу?`;
  const bulkOkText = scoreMode ? `${scoreMax} оноо тавих` : "Бүгдийг Тийм";

  const fillAllYes = () => {
    Modal.confirm({
      title: bulkTitle,
      width: 540,
      content: (
        <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
          <p style={{ marginBottom: 6 }}>
            Дараа нь асуудалтай мөрүүдээ «Үгүй» болгож зөрчлөө бүртгэнэ.
          </p>
          <p style={{ margin: 0, color: "#5A6478" }}>
            Аль хэдийн бөглөсөн мөр, бүртгэсэн зөрчил <b>хөндөгдөхгүй</b>.
          </p>
        </div>
      ),
      okText: bulkOkText,
      cancelText: "Болих",
      onOk: () => {
        const ids = new Set(blankRows.map((r) => r.id));
        revRef.current += 1;
        setItems((prev) =>
          prev.map((it) =>
            ids.has(it.id)
              ? {
                  ...it,
                  ...(scoreMode
                    ? { score: scoreMax }
                    : {
                        answer: "yes",
                        // `onAnswer`-ийн «yes» салаатай ИЖИЛ: зөрчлийн
                        // түүхтэй мөрийн төлөвийг хөндөхгүй.
                        fix_status: it.finding_text ? it.fix_status : null,
                      }),
                }
              : it,
          ),
        );
        message.success(
          `${ids.size} мөр бөглөгдлөө — асуудалтай мөрөө «Үгүй» болгоно уу`,
        );
      },
    });
  };

  // 0–5 оноо. 5-аас бага оноо нь журмаар зөрчил тул зөрчлийн талбарууд
  // нээгдэж, төлөв "Нээлттэй" болно.
  /* ── ДАРСАН ОНОО/ХАРИУЛТЫГ БУЦААХ (2026-10-15, хэрэглэгч:
     «хяналтын хуудас дээр дарсан оноогоо буцаах хэрэгтэй байна») ──
     ⚠ Өмнө нь ямар ч зам БАЙХГҮЙ байв: `Radio.Group` нь сонголтыг
       цэвэрлэх боломж өгдөггүй, «Тийм/Үгүй» нүд нь дахин дарахад ижил
       утгаа л дахин тавьдаг. Санамсаргүй дарсан оноо мөнхөд үлдэж,
       дүн (score_percent) ХУДАЛ болдог байв.
     ⚠ Сервер нь `null`-ыг ХҮЛЭЭЖ АВДАГ (`if "score" in row:
       r.score = row["score"]`) тул цэвэрлэлт бодитоор хадгалагдана —
       зөвхөн UI дутуу байсан.
     ⚠ ЗӨРЧЛИЙН БҮРТГЭЛИЙГ ЧИМЭЭГҮЙ УСТГАХГҮЙ: тайлбар бичигдсэн бол
       баталгаажуулж асууна (блоклохгүй — [[feedback_no_ai_invented_limits]]),
       `fix_status` нь тайлбартай үед хэвээр үлдэнэ. */
  const doClearRow = (row) => {
    setItem(row.id, {
      score: null,
      answer: null,
      // Тайлбар байвал зөрчлийн төлвийг үлдээнэ (түүх алдагдахгүй)
      fix_status: row.finding_text ? row.fix_status : null,
    });
    // Тэр мөрийн зөрчлийн маягт нээлттэй байвал хаана
    setFindingModal((cur) => (cur === row.id ? null : cur));
  };

  const clearRow = (row) => {
    if (rowLocked(row)) return;
    const hasFinding = !!(row.finding_text || "").trim();
    if (!hasFinding) {
      doClearRow(row);
      return;
    }
    Modal.confirm({
      title: "Оноог буцаах уу?",
      content:
        "Энэ мөрөнд зөрчил бүртгэгдсэн байна. Оноог буцаахад бүртгэсэн " +
        "зөрчлийн тайлбар, хариуцагч, хугацаа ХЭВЭЭР үлдэнэ — зөвхөн " +
        "оноо цэвэрлэгдэнэ. Зөрчлийг ч хасах бол тайлбарыг гараар " +
        "цэвэрлэнэ үү.",
      okText: "Буцаах",
      cancelText: "Болих",
      onOk: () => doClearRow(row),
    });
  };

  const onScore = (row, val) => {
    const patch = { score: val };
    if (val != null && val < scoreMax && !row.fix_status) {
      patch.fix_status = "open";
      prefillRule(row, patch);
      prefillWorkplace(row, patch);
    }
    setItem(row.id, patch);
  };

  /* ── Хариуцагчийн сонголт ────────────────────────────────────────────────
     Гараар ТН бичихийн оронд тухайн БН-ийн бүтцээс сонгоно. Хайлт нь нэр,
     ТН, регистрээр явна (сервер тал). Анхдагчаар шалгалтын нэгжээр
     хязгаарлагдана — өөр цехийн ажилтан САНАМСАРГҮЙ сонгогдохгүй.

     ⚠ ГЭХДЭЭ ЗОРИУДААР сонгож БОЛНО (2026-09-25, хэрэглэгчийн ажиглалт):
       зөрчлийг арилгах хүн тухайн хэсгийнх байх АЛБАГҮЙ — цахилгааны
       зөрчлийг Цахилгаан цех, өргөх механизмынхыг ЗМЗ арилгана. Нэгжээр
       хатуу хумибал тэр хүнийг сонгож чадахгүй тул зөрчил ЭЗЭНГҮЙ
       үлддэг байв. «Бүх нэгжээс» унтраалга асаахад хамрах хүрээ унана
       (сервер `all=1`, 2+ тэмдэгт хайлт шаардана). */
  /* «Бүх нэгжээс» сонгох ЦОНХ: цех → хэсэг → ажилтан.
     ⚠ Хайлтаар биш ЖАГСААЛТААР: шалгагч нь өөр цехийн ажилтны нэрийг
       мэдэхгүй байх нь энгийн зүйл — «цахилгааны хэсгийн мастер» гэж л
       мэднэ. Нэр бичүүлбэл тэр хүнийг ОГТ олохгүй. */
  const [respPicker, setRespPicker] = useState(null); // null | {rowId}
  const [pickTseh, setPickTseh] = useState({ code: "", name: "", parentId: null });
  const [pickSection, setPickSection] = useState({ code: "", name: "" });
  const [pickList, setPickList] = useState([]);
  const [pickLoading, setPickLoading] = useState(false);

  /**
   * ⚠ ЦЕХ/ХЭСГИЙГ САНАНА (2026-09-25, хэрэглэгчийн ажиглалт).
   *
   * Өмнө нь цонх нээгдэх бүрд сонголт ТЭГЛЭГДЭЖ байсан тул нэг шалгалтын
   * 5-10 зөрчилд ижил цехийн мастер оноох гэхэд цех → хэсгийг ДАХИН ДАХИН
   * сонгох шаардлагатай байв. Бодит урсгалд зөрчлүүд ихэвчлэн НЭГ ижил
   * нэгжид очдог (бүх цахилгааны зөрчил → Цахилгаан цех) тул өмнөх
   * сонголтыг үлдээвэл жагсаалт нь ШУУД бэлэн ирнэ.
   *
   * Өөр нэгж рүү шилжих нь нэг л клик — сонголт нь харагдаж байгаа тул
   * санамсаргүй буруу нэгжээс сонгох эрсдэлгүй.
   */
 

  /* Сонгосон нэгжийн ажилтныг БҮГДИЙГ татна (хайлтгүй).
     ⚠ Нэгж заасан үед сервер хамрах хүрээг ТҮҮГЭЭР тогтоодог тул
       `all` дамжуулах шаардлагагүй — өөр цехийн код өгөхөд л хангалттай. */
  useEffect(() => {
    const code = pickSection.code || pickTseh.code;
    if (!respPicker || !code) {
      setPickList([]);
      return;
    }
    let alive = true;
    setPickLoading(true);
    xabInspectionApi
      .searchWorkers({
        tseh_code: pickTseh.code || undefined,
        bn_code: pickSection.code || undefined,
        limit: 200,
      })
      .then((d) => alive && setPickList(d?.workers || []))
      .catch(() => alive && setPickList([]))
      .finally(() => alive && setPickLoading(false));
    return () => {
      alive = false;
    };
  }, [respPicker, pickTseh.code, pickSection.code]);

  


  /* ЗӨРЧЛИЙН МӨРИЙН ТАЛБАРУУД — серверийн PUT хүлээж авдаг бүх талбар.
   *
   * ⚠ ЦАГААН ЖАГСААЛТ БИЧИЖ БОЛОХГҮЙ (2026-09-30-ны гэмтлийн сургамж).
   *   Өмнө нь энд 17 талбар ХАТУУ бичигдсэн байсан тул зөрчлийн маягтын
   *   «авсан арга хэмжээ» (`measure_*`), зөрчсөн заалт, мэдээллийн
   *   ангилал, эрсдэлийн түвшин, төлөвлөсөн арга хэмжээ, хариуцагч БН,
   *   олон сонголтын талбарууд БҮГД ЧИМЭЭГҮЙ хаягдаж байв. Улмаас:
   *     · жагсаалтын «Авсан арга хэмжээ» багана үргэлж хоосон,
   *     · бүртгэсэн зөрчлийг ЗАСАХ гэж нээхэд талбарууд хоосон
   *       (учир нь баазад хэзээ ч бичигдээгүй).
   *   Баримт: шинэ 64 бүртгэл дээр `measure_text` = 0, `remedy_plan` = 0.
   *
   * ⚠ ЯГ ИЖИЛ алдаа `createHazard`-д 2026-09-19-нд гарсан. Маягт өсөх
   *   тутам цагаан жагсаалт чимээгүй хоцордог — тиймээс шинэ талбар
   *   нэмэхдээ ЭНД бас нэмнэ.
   */
  const FINDING_FIELDS = [
    "finding_text", "violated_rule",
    "serious_violation_id", "serious_violation_ids",
    "violation_kind",
    "violation_type_id", "violation_type_text",
    "violation_type_ids", "violation_type_texts",
    "violation_clause_id", "violation_clause_text",
    "violation_clause_ids", "violation_clause_texts",
    // ЗӨРЧИЛ ГАРГАСАН ажилтан — хариуцагчаас ӨӨР талбар. Нэр, албан
    // тушаалыг сервер өөрөө лавлахаас хуулна (денормал).
    "violator_tn",
    // Гаргагчид ногдуулсан шийтгэл. Текстийг ЭНД хуулж явуулна —
    // лавлах хожим засагдсан ч бүртгэл тухайн үеийнхээрээ үлдэнэ.
    "penalty_id", "penalty_text",
    // АВСАН АРГА ХЭМЖЭЭ — жагсаалтын багана үүнээс бүрдэнэ.
    "measure_id", "measure_text",
    "info_class_id", "info_class_text", "haz_level_text",
    // Эрсдэл: нөхцөлийн id · эрсдэлтэй эсэх · түвшин (2026-09-30)
    "haz_level_id", "is_risk", "severity",
    "remedy_plan", "remedy_due_note",
    /* Гүйцэтгэх хугацааны ТӨРӨЛ (2026-11-01):
       standing | urgent | urgent_standing | dated.
       ⚠ «standing» (тогтмол) үед сервер `due_date`-ийг
         ШААРДАХГҮЙ — тийм зөрчил ноорог болохгүй. */
    "due_kind",
    /* Хариуцагчийн ТӨРӨЛ (2026-11-01): person | first_line | ita.
       ⚠ Бүлэгт хуваарилсан үед сервер `responsible_tn` шаардахгүй. */
    "responsible_kind",
    "responsible_bn_code", "responsible_bn_name",
    "fix_status", "closed_note",
    /* ⚠ `fix_note` · `fix_category`-Г ОРУУЛЖ БОЛОХГҮЙ. Тэд нь ХАРИУЦАГЧИЙН
       биелэлтийн тайлбар бөгөөд өөр урсгалаар (`my_assignment` /
       `item_assignment`) бичигддэг. Шалгах маягт тэднийг зөвхөн ХАРУУЛДАГ
       («Биелэлт ирсэн» чип) — гэтэл payload-д оруулбал шалгагчийн
       хуудсан дээрх ХОЦОРСОН хуулбар нь хариуцагчийн сая бичсэн
       тайлбарыг ДАРЖ УСТГАНА. */
  ];
  // Огноо — серверийн хүлээж авдаг форматад хөрвүүлнэ
  const FINDING_DATE_FIELDS = ["due_date", "remedy_start_date"];

  const buildPayload = () => ({
    items: items.map((it) => {
      const out = {
        id: it.id,
        /* ⚠⚠ ЗААЛТЫН ТАНИХ ТЭМДГИЙГ ЗААВАЛ ЯВУУЛНА (2026-11-05).
           Өмнө нь зөвхөн `id` явдаг байсан тул III шатанд сервер
           гишүүнд ӨӨРИЙН мөр үүсгэхэд заалт нь мэдэгдэхгүй, улмаас
           ТЕКСТГҮЙ «хоосон үнэлгээ» мөрүүд үүсч байв (хэмжсэн: нэг
           шалгалтад 105). Сервер талд ч хамгаалалт бий (эх мөрөөс
           хуулж авна, олдохгүй бол мөр үүсгэхгүй) — энэ нь түүний
           анхдагч эх сурвалж. */
        template_item_id: it.template_item_id ?? null,
        answer: it.answer || null,
        score: it.score == null ? null : it.score,
        responsible_tn: it.responsible_tn || null,
      };
      /* ⚠ `f in it` ГЭЖ ШАЛГАНА. Серверээс ирээгүй талбарт `null`
         явуулбал баазад хадгалагдсан утгыг ДАРЖ УСТГАНА (сервер ч
         `if _f in row` гэж шалгадаг тул байхгүйд хүрэхгүй). */
      FINDING_FIELDS.forEach((f) => {
        if (f in it) out[f] = it[f] ?? null;
      });
      FINDING_DATE_FIELDS.forEach((f) => {
        if (f in it) {
          out[f] = it[f]
            ? moment(it[f]).format("YYYY-MM-DD HH:mm:ss")
            : null;
        }
      });
      return out;
    }),
  });

  // ⚠ ЭНД «аль хэдийн хадгалж байвал буц» гэсэн хамгаалалт ТАВЬЖ БОЛОХГҮЙ.
  //   Хаах үйлдэл нь `const ok = await doSave(true); if (!ok) return;` гэж
  //   ажилладаг тул автомат хадгалалт тэр агшинд явж байвал хаалт ЧИМЭЭГҮЙ
  //   тасалдана. Давхцлаас сэргийлэх шалгалт нь автомат хадгалалтын
  //   interval дотор байна — тэр л буулт хийх ёстой тал.
  /** Нэг заалтад ӨӨР зөрчил нэмэх (2026-10-26).
   *
   * Нэг үзүүлэлт дээр хэд хэдэн зөрчил илрэх нь бодит тохиолдол
   * (хэрэглэгчийн хүсэлт). Backend нь `id`-гүй мөрийг ШИНЭЭР үүсгэдэг
   * тул ижил `template_item_id`-тай хоёр дахь мөр хадгалагдана.
   *
   * ⚠ Дараалал ЧУХАЛ:
   *   ① одоогийн засварыг хадгална — `load()` төлөвийг дарж бичнэ
   *   ② шинэ мөрийг СЕРВЕРТ үүсгэнэ (`id` явуулахгүй)
   *   ③ дахин татна — мөр жинхэнэ `id`-тай ирэх тул зураг хавсаргах,
   *     засах зэрэг бүх үйлдэл шууд ажиллана
   *
   * ⚠ ДҮГНЭЛТЭД НӨЛӨӨЛӨХГҮЙ: сервер талын `_eval_rows` нь нэг
   *   `template_item_id`-г НЭГ л удаа тоолдог (a734f00). Зөрчил нь
   *   жагсаалтад тус бүрээр харагдана, харин Хавсралт №4-ийн хил
   *   (≥8 ҮГҮЙ) ба онооны хуваарь нь үзүүлэлтээр хэвээр —
   *   хэрэглэгчийн шийдвэр.
   */
  const addExtraFinding = async (row) => {
    if (rowLocked(row)) return;
    /* ── ХООСОН МӨР АЛЬ ХЭДИЙН БАЙВАЛ ШИНИЙГ ҮҮСГЭХГҮЙ (2026-11-02) ──
       Хэрэглэгч: «өөр зөрчил нэмэх гэх үед энэ дахин үүсэж байгааг
       засах». Товч нь зөвхөн БӨГЛӨГДСӨН мөрөн дээр гардаг тул
       хэрэглэгч эхний мөрөн дээрээ дахин дарж болдог — тэр болгонд
       шинэ ХООСОН мөр нэмэгдэж хуримтлагдана. ХЭМЖСЭН: нэг
       үзүүлэлтэд олон мөртэй 26 бүлэг, жш шалгалт 6975 · үзүүлэлт 40
       нь 4 мөртэй, 2 нь хоосон.
       Бөглөөгүй мөр байвал ТҮҮНИЙГ нээнэ — хэрэглэгч юу хийх ёстойг
       шууд харна, давхардал ч үүсэхгүй.
       ⚠ Сервер талд ч ижил хамгаалалт бий (`inspection.py`) — энэ нь
         зөвхөн тав тухын давхарга, жинхэнэ идемпотентлэг нь тэнд. */
    const blank = items.find(
      (x) =>
        x.id !== row.id &&
        x.template_item_id != null &&
        x.template_item_id === row.template_item_id &&
        !String(x.finding_text || "").trim(),
    );
    if (blank) {
      setFindingModal(blank.id);
      message.info("Энэ заалтад бөглөөгүй зөрчлийн мөр аль хэдийн байна");
      return;
    }
    // ⚠ `doSave` өөрөө `saving`-ыг тохируулж, finally дотроо
    //   цэвэрлэдэг тул түүнээс ӨМНӨ туг тавивал дараа нь
    //   цэвэрлэгдэж, үлдсэн хоёр дуудлагын хугацаанд UI
    //   түгжээгүй үлдэнэ. Тиймээс ДАРАА нь тавина.
    const ok = await doSave(true);
    if (!ok) return;
    setSaving(true);
    try {
      await xabInspectionApi.update(id, {
        items: [
          {
            // `id` ОГТ явуулахгүй — backend шинэ мөр үүсгэнэ
            template_item_id: row.template_item_id ?? null,
            group_name: row.group_name ?? null,
            item_no: row.item_no ?? null,
            item_text: row.item_text ?? null,
            sort_order: row.sort_order ?? null,
            answer: "no", // зөрчил = «Үгүй»
          },
        ],
      });
      await load();
      message.success("Энэ заалтад өөр зөрчил нэмэгдлээ");
    } catch (e) {
      message.error(
        e?.response?.data?.message || "Зөрчил нэмэхэд алдаа гарлаа",
      );
    } finally {
      setSaving(false);
    }
  };

  /** ХООСОН нэмэлт зөрчлийн мөрийг устгах (2026-11-02).
   *
   * Хэрэглэгч: «өөр зөрчил бүртгэх гээд зөрчил оруулахгүй тохиолдол
   * гарч байна, энэ үед устгах буцаах хэрэгтэй байна».
   *
   * ⚠ ХАМГААЛАЛТ нь СЕРВЕР талд: зөвхөн ХООСОН мөр, зөвхөн тухайн
   *   үзүүлэлтэд ӨӨР мөр байх үед. Үзүүлэлтийн цорын ганц мөрийг
   *   устгавал тэр үзүүлэлт хуудаснаас алга болно. UI нь товчийг
   *   нуудаг ч тэр нь зөвхөн тав тух.
   */
  const removeExtraRow = async (row) => {
    if (!row?.id || rowLocked(row)) return;
    setSaving(true);
    try {
      await xabInspectionApi.deleteItemRow(row.id);
      await load();
      message.success("Хоосон мөр устгагдлаа");
    } catch (e) {
      message.error(
        e?.response?.data?.message || "Мөр устгахад алдаа гарлаа",
      );
    } finally {
      setSaving(false);
    }
  };

  /** Чеклэсэн хүмүүсийг даалгавар болгон синк хийнэ.
   *
   * ⚠ ЗӨВХӨН бүлгийн төрөлтэй (`first_line` · `ita`) мөрд. `person`
   *   үед хуучин нэг хариуцагчийн урсгал хэвээр.
   * ⚠ ӨӨРЧЛӨГДСӨН мөрд л дуудна: endpoint нь багцыг БҮХЭЛД НЬ
   *   орлуулдаг тул дэмий дуудвал гүйцэтгэлийн бичилт дэмий
   *   дахин бичигдэнэ.
   * ⚠ ХЭВЭЭР үлдсэн хүний `id`-г дамжуулна — endpoint нь `id`-тай
   *   мөрийг ЗАСДАГ (гүйцэтгэлийн талбарт хүрэхгүй), `id`-гүйг
   *   ШИНЭЭР үүсгэдэг. Үгүй бол мастерын оруулсан хувь, зураг
   *   чимээгүй устана.
   * ⚠ Алдааг ЗАЛГИХГҮЙ — хэрэглэгч «хадгалагдлаа» гэж уншаад
   *   даалгавар үүсээгүй байвал зөрчил эзэнгүй үлдэнэ.
   */
  const syncAssignments = async () => {
    const GROUP_KINDS = ["first_line", "ita"];
    const targets = (items || []).filter(
      (it) =>
        it.id &&
        GROUP_KINDS.includes(it.responsible_kind) &&
        Array.isArray(it.responsible_tns),
    );
    for (const it of targets) {
      const want = it.responsible_tns.map(String);
      const have = (it.assignments || [])
        .filter((a) => a && a.responsible_tn)
        .map((a) => String(a.responsible_tn));
      const same =
        want.length === have.length &&
        want.every((t) => have.includes(t));
      if (same) continue;
      const byTn = new Map(
        (it.assignments || []).map((a) => [String(a.responsible_tn || ""), a]),
      );
      try {
        await xabItemAssignmentApi.save(it.id, {
          items: want.map((tn) => {
            const ex = byTn.get(tn);
            return ex
              ? {
                  id: ex.id,
                  responsible_tn: tn,
                  fix_category: ex.fix_category,
                  task_text: ex.task_text,
                  due_date: ex.due_date,
                }
              : {
                  responsible_tn: tn,
                  due_date: it.due_date || null,
                  task_text: it.finding_text || null,
                };
          }),
        });
      } catch (e) {
        message.error(
          (e?.response?.data?.message || "Даалгавар хадгалахад алдаа") +
            " (№" + (it.item_no ?? it.id) + ")",
        );
      }
    }
  };

  const doSave = async (silent) => {
    // Хүсэлт явахын ӨМНӨХ дугаар. Хариу ирэх хооронд бичсэн засвар нь
    // дугаарыг урагшлуулах тул «хадгалагдсан» гэж тэмдэглэгдэхгүй.
    const rev = revRef.current;
    savingRef.current = true;
    setSaving(true);
    try {
      const res = await xabInspectionApi.update(id, buildPayload());
      savedRevRef.current = rev;
      /* ⚠ ТТ ЧИМЭЭГҮЙ ХАЯГДСАНЫГ ХЭЛНЭ (2026-10-30). Сервер нь
         `device_id`-г raw SQL-ээр бичдэг бөгөөд багана баазад
         үүсээгүй бол бичилт БҮТЭХГҮЙ. Хэлэхгүй бол хэрэглэгч ТТ-гээ
         сонгоод «хадгаллаа» гэж уншаад, дахин татахад ТТ алга —
         «дата алга болов» гэсэн эргэлзээ төрнө
         ([[feedback_save_to_db_silent_false]]). */
      if (res && res.due_kind_saved === false) {
        message.warning({
          content:
            "Хугацааны төрөл хадгалагдсангүй (баазад багана "
            + "үүсээгүй). Админд мэдэгдээрэй — бусад талбар "
            + "хадгалагдсан.",
          duration: 8,
        });
      }
      if (res && res.device_saved === false) {
        /* ⚠ `silent` (автомат хадгалалт) үед Ч харуулна: чимээгүй
           алдагдаж байгаа нь ЯГ УРЬДЧИЛЖ мэдэгдэх ёстой зүйл. */
        message.warning({
          content:
            "Тоног төхөөрөмж хадгалагдсангүй (баазад багана үүсээгүй). " +
            "Админд мэдэгдээрэй — бусад талбар хадгалагдсан.",
          duration: 8,
        });
      } else if (!silent) message.success("Хадгаллаа");
      /* ── ЧЕКЛЭСЭН ХҮМҮҮС → ТУС БҮРД ДААЛГАВАР (2026-11-03) ──────
         Хэрэглэгч: «тухайн зөрчлийг хэд хэдэн хүнд хариуцуулж болох
         юм» + «тус бүрд даалгавар» (2026-11-02-ны шийдвэр).
         `responsible_tns` нь баазын багана БИШ тул `buildPayload`-д
         ороогүй — энд ТУСДАА endpoint-оор синк хийнэ. */
      await syncAssignments();
      onSaved?.();
      return true;
    } catch (e) {
      // ⚠ Амжилтгүй бол дугаарыг урагшлуулахгүй — дараагийн автомат
      //   хадгалалт дахин оролдоно, засвар нь алдагдахгүй.
      // Backend-ийн шалтгааныг харуулна (жш: «Зөвхөн бүртгэл оруулсан
      // эсвэл шалгасан хүн засварлана», «Хаагдсан шалгалтыг…») — эс бөгөөс
      // хэрэглэгч яагаад засагдахгүй байгааг мэдэхгүй.
      if (!silent)
        message.error(e?.response?.data?.message || "Хадгалахад алдаа гарлаа");
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // Рендэр бүрд ХАМГИЙН СҮҮЛИЙН `doSave`-ыг рефэд тавина (stale closure)
  saveRef.current = doSave;

  /** ЧИМЭЭГҮЙ АВТОМАТ ХАДГАЛАЛТ — 30 секунд тутам, өөрчлөлт байвал.
   *
   *  ⚠ Мэдэгдэл ГАРГАХГҮЙ (`silent`) — 30 секунд тутам «Хадгаллаа» гэж
   *    гарвал шалгагчийн анхаарлыг сарниулна. Амжилтгүй бол засварын дугаар
   *    урагшлахгүй тул дараагийн мөчлөгт дахин оролдоно, мөн хуудсаас
   *    гарахад доорх анхааруулга ажиллана.
   *
   *  ⚠ Хаагдсан шалгалтад хийхгүй — засвар хийх боломжгүй. */
  useEffect(() => {
    if (closed || loading) return undefined;
    const t = setInterval(() => {
      if (isDirty() && !savingRef.current) saveRef.current?.(true);
    }, AUTOSAVE_MS);
    return () => clearInterval(t);
  }, [closed, loading, id]);

  /** Хадгалаагүй засвартай байхад хуудсаас гарах гэвэл анхааруулна.
   *
   *  Автомат хадгалалтын хоорондох 30 секундын цонхыг хаана. Браузер
   *  өөрийн стандарт бичвэрээ харуулдаг — өөрчлөх боломжгүй. */
  useEffect(() => {
    const warn = (e) => {
      if (!isDirty()) return undefined;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);

  // Журам: бүх үзүүлэлт бөглөгдсөн байх; зөрчил бүрт тайлбар + арилгах хугацаа
  // + хариуцагч заавал. Хаахаас өмнө дутууг тоолж харуулна.
  /** НЭГ ЗӨРЧЛИЙН дутуу талбарууд — монгол нэрээр.
   *
   * Хэрэглэгч (2026-10-30): «Зөрчлийн бүртгэл дээр бөглөх талбар заавал
   * байхгүй бол ноорог төлөвтэй илгээгдэж байна».
   *
   * ⚠ ЯАГААД ХЭРЭГТЭЙ ВЭ: сервер нь дутуу зөрчлийг `fix_status =
   *   'draft'` болгодог (ЗӨВ — хэрэглэгчийн 2026-10-08-ны шийдвэр,
   *   хадгалахыг зогсоохгүй). Гэвч ЗӨРЧЛИЙН ЦОНХНЫ «Хадгалах» нь юу ч
   *   хэлэхгүй цонхоо хаадаг тул хэрэглэгч бүртгэл БҮРЭН болсон гэж
   *   ойлгоно. ХЭМЖСЭН (бодит бааз, 2026-10-30): ноорог зөрчил 138 —
   *   I шат 59 · III 35 · II 23, зөвхөн өнөөдөр 19. Дутсан талбар:
   *   хугацаа 111 · ангилал 107 · хариуцагч 99 · тайлбар 50.
   *
   * ⚠ Серверийн `FINDING_REQUIRED`-ТАЙ ИЖИЛ байх ЁСТОЙ (`inspection.py`
   *   → finding_text · due_date · responsible_tn · violation_kind).
   *   Зөрүүтэй бол хэрэглэгч «бөглөсөн» гэж бодоод сервер ноорог гэж
   *   үзсээр байна — `closeIssues()`-д ч ижил тайлбар бий. */
  const findingMissing = (row) => {
    const out = [];
    if (!String(row.finding_text || "").trim()) out.push("тайлбар");
    /* ⚠ «ТОГТМОЛ» зөрчилд ДУУСАХ ОГНОО БАЙХГҮЙ
       (2026-11-01). Сервер ч `finding_due.needs_due_date`-аар түүнийг
       чөлөөлдөг тул енд шаардвал анхааруулга НЬ ХУДАЛ болно. */
    if (!row.due_date && row.due_kind !== "standing")
      out.push("арилгах хугацаа");
    /* ⚠ БҮЛЭГТ ХУВААРИЛСАН үед ТН байхгүй нь ЗӨВ (2026-11-01).
       Сервер ч `finding_due.needs_responsible_tn`-аар чөлөөлдөг тул
       енд шаардвал анхааруулга НЬ ХУДАЛ болно. */
    if (
      !String(row.responsible_tn || "").trim() &&
      !["first_line", "ita"].includes(row.responsible_kind)
    )
      out.push("хариуцагч");
    if (!String(row.violation_kind || "").trim())
      out.push("ангилал (ноцтой эсэх)");
    return out;
  };

  const closeIssues = () => {
    const out = [];
    const unanswered = items.filter((i) =>
      scoreMode ? i.score == null : !(i.answer === "yes" || i.answer === "no")
    );
    if (unanswered.length) out.push(`${unanswered.length} үзүүлэлт бөглөгдөөгүй`);

    const findings = items.filter(isFinding);
    const noText = findings.filter((i) => !String(i.finding_text || "").trim());
    if (noText.length) out.push(`${noText.length} зөрчилд тайлбар бичээгүй`);
    // ⚠ «Тогтмол» зөрчилд огноо шаардахгүй (2026-11-01).
    const noDue = findings.filter(
      (i) => !i.due_date && i.due_kind !== "standing",
    );
    if (noDue.length) out.push(`${noDue.length} зөрчилд арилгах хугацаа заагаагүй`);
    // ⚠ Бүлэгт хуваарилсан зөрчилд ТН шаардахгүй (2026-11-01).
    const noResp = findings.filter(
      (i) =>
        !String(i.responsible_tn || "").trim() &&
        !["first_line", "ita"].includes(i.responsible_kind),
    );
    if (noResp.length) out.push(`${noResp.length} зөрчилд хариуцагч заагаагүй`);
    /* ⚠ ЗӨРЧЛИЙН АНГИЛАЛ (2026-10-08, хэрэглэгчийн шийдвэр). Энэ
       жагсаалт нь серверийн `FINDING_REQUIRED`-тай ИЖИЛ байх ёстой —
       зөрүүтэй бол хэрэглэгч «бөглөсөн» гэж бодоод сервер ноорог гэж
       үзсээр, хаалт тайлбаргүй саатна. */
    const noKind = findings.filter(
      (i) => !String(i.violation_kind || "").trim(),
    );
    if (noKind.length)
      out.push(`${noKind.length} зөрчилд ангилал (ноцтой эсэх) заагаагүй`);
    return out;
  };

  /** «Хадгалах» — хадгална, ДАРАА нь дутуу бөглөлтийг сануулна.
   *
   * ⚠ Анхааруулга нь ХАДГАЛАХЫГ ЗОГСООХГҮЙ: шалгагч 20-30 үзүүлэлтийг
   *   хэсэгчлэн бөглөж, дундуур нь хадгалдаг. Блоклобол ажил нь алдагдана.
   *   Харин зөрчилд хугацаа/хариуцагч заагаагүй бол журмын шаардлага
   *   биелэхгүй тул ХААХ үед `closeIssues()` хатуу шалгасаар байна.
   *
   * ⚠ Энэ анхааруулга алга болсон байсныг сэргээв (2026-09-24). Урьд нь
   *   зөвхөн ХААХ дээр гардаг байсан тул шалгагч хадгалаад орхиход
   *   хариуцагчгүй зөрчил чимээгүй үлддэг байв.
   */
  const saveWithHint = async () => {
    const ok = await doSave(false);
    if (!ok) return;
    const issues = closeIssues().filter((t) => !t.includes("бөглөгдөөгүй"));
    if (issues.length) {
      message.warning({
        content: (
          <span>
            Хадгалсан боловч дутуу байна: <b>{issues.join(", ")}</b>. Шалгалтыг
            ХААХЫН өмнө эдгээрийг бөглөнө үү.
          </span>
        ),
        duration: 6,
      });
    }
  };

  // Хавсралт №4-ийн САНАЛ хэсэг (сахилгын шийтгэл / сайшааж урамшуулах)
  const proposalRef = useRef({ discipline: "", reward: "" });

  /* ── ХААГДСАН ШАЛГАЛТЫГ БУЦААХ ────────────────────────────────────
     ⚠ `Modal.confirm`-ийн `onOk`-д алдаа ШИДЭХГҮЙ: шидсэн алдааг antd
       барьдаггүй тул консолд «Uncaught (in promise)» гарч, жинхэнэ
       алдаануудын дунд дуу чимээ болдог. Оронд нь УДИРДЛАГАТАЙ
       Modal — «Дахин нээх» товч шалтгаан бичигдтэл идэвхгүй байна.
       Ингэснээр буруу төлөвт дарах боломж ҮҮСЭХГҮЙ. */
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenNote, setReopenNote] = useState("");
  const [reopenBusy, setReopenBusy] = useState(false);

  const doReopen = () => {
    setReopenNote("");
    setReopenOpen(true);
  };

  const submitReopen = async () => {
    const note = reopenNote.trim();
    if (!note) return;
    setReopenBusy(true);
    try {
      await xabInspectionApi.reopen(id, note);
      message.success("Шалгалтыг буцаалаа");
      setReopenOpen(false);
      load();
    } catch (e) {
      message.error(e?.response?.data?.message || "Буцааж чадсангүй.");
    } finally {
      setReopenBusy(false);
    }
  };

  const reallyClose = async (force = false) => {
    const ok = await doSave(true);
    if (!ok) return;
    try {
      const res = await xabInspectionApi.close(id, {
        force,
        proposal_discipline: proposalRef.current.discipline,
        proposal_reward: proposalRef.current.reward,
      });
      const m = EVAL_META[res.evaluation];
      const detail = res.scored_count
        ? `${res.score_percent}% (${res.scored_count} үзүүлэлт × 5 оноо)`
        : `Тийм ${res.yes_count} / Үгүй ${res.no_count}`;
      message.success(`Хаагдлаа. Үнэлгээ: ${m ? m.label : "—"} — ${detail}`);
      onClosed?.(res);
      load();
    } catch (e) {
      /* 409 — комиссын оноогдсон бүлэг бөглөгдөөгүй. Удирдагч шийднэ:
         буцаж хүлээх үү, force-оор хаах уу (гишүүн ирээгүй г.м.). */
      const inc = e?.response?.status === 409 ? e.response.data?.incomplete_groups : null;
      if (inc?.length) {
        Modal.confirm({
          title: "Комиссын гишүүний бүлэг бөглөгдөөгүй байна",
          width: 520,
          content: (
            <div>
              <p style={{ marginBottom: 8 }}>
                Журам 2.5.3 — гишүүн тус бүр өөрийн бүлгийг бөглөх ёстой:
              </p>
              <ul style={{ paddingLeft: 18, margin: 0, color: "#B91C1C" }}>
                {inc.map((g) => (
                  <li key={g.group_id}>
                    <b>{g.group_name}</b> — {g.member_name}: {g.unanswered} мөр хариултгүй
                  </li>
                ))}
              </ul>
            </div>
          ),
          okText: "Ингээд хаах",
          okButtonProps: { danger: true },
          cancelText: "Хүлээх",
          onOk: () => reallyClose(true),
        });
        return;
      }

      /* ── АРИЛГААГҮЙ ЗӨРЧИЛ (2026-10-08) ──────────────────────────
         Хэрэглэгчийн дүрэм: зөрчил арилгаагүй үед шалгалт хаагдахгүй.
         ⚠ «Ингээд хаах» товчийг ЭНД САНАЛ БОЛГОХГҮЙ: сервер force-ийг
           зөвхөн ХАБЭА-д зөвшөөрдөг бөгөөд энэ бол журмын шаардлага,
           гишүүн ирээгүй гэх мэт зохион байгуулалтын саатал биш.
           Жагсаалтыг харуулж, юуг нь дуусгахыг заана. */
      const un =
        e?.response?.status === 409
          ? e.response.data?.unresolved_findings
          : null;
      if (un?.length) {
        const total = e.response.data?.unresolved_count || un.length;
        /* ⚠ ХОЁР ӨӨР АЖИЛ: `submitted` нь ШАЛГАГЧ хүлээн авах (одоо
           шийдэж болно), бусад нь ХАРИУЦАГЧ арилгах (хүлээх). Нэг
           жагсаалтад хольвол хэрэглэгч өөрийн хийх ажлаа олохгүй. */
        const waitMe = un.filter((f) => f.fix_status === "submitted");
        const waitThem = un.filter((f) => f.fix_status !== "submitted");
        /* ⚠ ШИНЭ ТАБААР — шалгах хуудсанд хадгалаагүй өөрчлөлт байж
           болох тул хуудсыг сольж болохгүй. Хүлээн авах нь зөрчлийн
           дэлгэрэнгүй дээр (`/incident/violation/:id`). */
        const row = (f) => (
          <li key={f.id} style={{ marginBottom: 4 }}>
            <b>{f.item_no}</b>. {f.text}
            <span style={{ color: "#8A94A6" }}>
              {" "}
              — {FIX_META[f.fix_status] || f.fix_status}
            </span>{" "}
            <a
              href={`/incident/violation/${f.id}`}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: 11.5, whiteSpace: "nowrap" }}
            >
              нээх <i className="fas fa-arrow-up-right-from-square" />
            </a>
          </li>
        );
        Modal.info({
          title: `Арилгаагүй ${total} зөрчил байна`,
          width: 620,
          content: (
            <div style={{ fontSize: 12.5 }}>
              {waitMe.length ? (
                <>
                  <p style={{ margin: "0 0 4px", color: "#B45309" }}>
                    <b>Та хүлээн авах ёстой ({waitMe.length})</b> —
                    хариуцагч биелэлтээ илгээсэн. Зөрчлийг нээж
                    «Хүлээн авах» дарсны дараа шалгалт хаагдана.
                  </p>
                  <ul style={{ paddingLeft: 18, margin: "0 0 10px" }}>
                    {waitMe.map(row)}
                  </ul>
                </>
              ) : null}
              {waitThem.length ? (
                <>
                  <p style={{ margin: "0 0 4px", color: "#B91C1C" }}>
                    <b>Биелэлт хүлээгдэж буй ({waitThem.length})</b> —
                    хариуцагч арилгаагүй. <b>Ноорог</b> нь заавал талбар
                    дутуу гэсэн үг.
                  </p>
                  <ul style={{ paddingLeft: 18, margin: 0 }}>
                    {waitThem.map(row)}
                  </ul>
                </>
              ) : null}
              {total > un.length ? (
                <p style={{ marginTop: 8, color: "#8A94A6" }}>
                  …бусад {total - un.length} зөрчил
                </p>
              ) : null}
            </div>
          ),
          okText: "Ойлголоо",
        });
        return;
      }
      message.error(e?.response?.data?.message || "Хаахад алдаа гарлаа");
    }
  };

  /* ══════════════ ДҮНГИЙН ХУРАЛ (ХЭМАБМТ-06 4.1.4.3) ══════════════
     III/IV шатны үнэлгээг шалгагч дангаараа тогтоохгүй:
       ① шалгагч  «Дүнгийн хуралд илгээх»  (status нь open ХЭВЭЭР)
       ② комиссын дарга «Хурлаар батлах»   (зөвхөн энд closed болно)
     Бусад шат (I · II · зорилтот · байнгын · ээлжит бус) нь үнэлгээгүй
     тул «Дүгнэж хаах» товчоороо шууд хаагдана. */
  const needMeeting = !!insp?.stage_requires_meeting;
  const meetState = insp?.meeting_state || null;

  /* ⚠ III ШАТАНД ИЛГЭЭЛТ ХОЁР ТҮВШИН (2026-11-05, хэрэглэгч: «үнэлгээ
     хийх бүлэг болгоныг үнэлгээ хийх хүн бүр үнэлнэ, үнэлгээгээ болсон
     гэж үзсэн үед дүнгиын хуралд илгээнэ»):
       ① ГИШҮҮН — ӨӨРИЙН үнэлгээг илгээнэ (дундаж оноо нь комиссын
          мөрөнд бичигдэнэ), шалгалт нь хуралд ЯВАХГҮЙ;
       ② удирдагч · ХАБЭА — шалгалтыг бүхэлд нь хуралд илгээнэ.
     Серверийн шийдвэр нь ЭРХЭЭР (`is_member && !is_leader`) тул UI нь
     зөвхөн ТАЙЛБАР ба мессежээ тааруулна — хоёр тал зөрвөл хэрэглэгч
     «юу болсон» нь мэдэгдэхгүй. */
  const memberSubmit = !!commission?.is_member && !commission?.is_leader;

  const doSubmitMeeting = () => {
    Modal.confirm({
      title: memberSubmit
        ? "Өөрийн үнэлгээг дүнгийн хуралд илгээх үү?"
        : "Дүнгийн хуралд илгээх үү?",
      width: 560,
      content: (
        <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
          {memberSubmit ? (
            <>
              <p style={{ marginBottom: 6 }}>
                ТАНЫ үнэлгээ (оноонуудын дундаж) комиссын бүртгэлд
                тэмдэглэгдэж, дүнгийн хуралд танилцуулагдана. Бусад
                гишүүдийн үнэлгээтэй <b>дундажлагдаж</b> нэгдсэн үнэлгээ
                гарна.
              </p>
              <p style={{ margin: 0, color: "#B45309" }}>
                Шалгалт ХААГДАХГҮЙ — та оноогоо засаад дахин илгээж болно.
              </p>
            </>
          ) : (
            <>
              <p style={{ marginBottom: 6 }}>
                Үр дүн нь хяналтын <b>нарийн бичгийн даргад</b> илгээгдэж,
                дүнгийн хуралд танилцуулагдана (ХЭМАБМТ-06 4.1.4.3 —
                хаалтын хурал болохоос <b>5 цагийн өмнө</b>).
              </p>
              <p style={{ margin: 0, color: "#B45309" }}>
                Шалгалт ХААГДАХГҮЙ — үнэлгээг комиссын дарга хурлаар
                тогтооно.
              </p>
            </>
          )}
        </div>
      ),
      okText: "Илгээх",
      cancelText: "Болих",
      onOk: async () => {
        const ok = await doSave(true);
        if (!ok) return;
        try {
          const res = await xabInspectionApi.submitToMeeting(id, {});
          /* ⚠ СЕРВЕРИЙН МЕССЕЖИЙГ харуулна: гишүүний илгээлтэд «N/M
             гишүүн илгээсэн» гэсэн тоо ирдэг — үүнийг хаявал гишүүн
             бусад хэд хүлээгдэж байгааг мэдэхгүй. */
          message.success(
            res?.message ||
              (memberSubmit
                ? "Таны үнэлгээ илгээгдлээ"
                : "Дүнгийн хуралд илгээлээ"),
          );
          load();
        } catch (e) {
          message.error(e?.response?.data?.message || "Илгээхэд алдаа гарлаа");
        }
      },
    });
  };

  const doWithdrawMeeting = () => {
    Modal.confirm({
      title: "Хуралд илгээснээ буцаах уу?",
      content: (
        <div style={{ fontSize: 12.5 }}>
          Үр дүн нь хуралд танилцуулагдахаа болино. Үргэлжлүүлэн бөглөөд
          дахин илгээж болно.
        </div>
      ),
      okText: "Буцаах",
      cancelText: "Болих",
      onOk: async () => {
        try {
          await xabInspectionApi.withdrawFromMeeting(id);
          message.success("Илгээснээ буцаалаа");
          load();
        } catch (e) {
          message.error(e?.response?.data?.message || "Буцаахад алдаа гарлаа");
        }
      },
    });
  };

  const doApproveMeeting = () => {
    /* ⚠ Modal.confirm-ийн content дотор state хэрэглэхгүй — ref-ээр
       уншина. Modal нь нэг л удаа рендэрлэгддэг тул `useState` нь
       шинэчлэгдсэн утгыг `onOk`-д хүргэхгүй. */
    /* ── ДУГААРЛАЛТЫН ЗАГВАР (хэрэглэгч 2026-11-05: «Хурлын протоколын
       дугаарыг бичих дугаарлалт хийх загвар харуулах») ───────────────
       Талбар нь байсан ч ХООСОН бөгөөд ямар хэлбэрээр бичихийг заадаггүй
       байв. Үр дүнд нь баазад бичигдсэн ЦОРЫН ГАНЦ дугаар «26/10 ХАБ»
       (цех 55), placeholder нь «2026/14» — хоёр өөр хэлбэр.

       Загварыг ЗОХИООГҮЙ, бодит баримтаас авсан. Хоёулаа САР СУУРЬТАЙ:
         · цех 25-ын 2026/08 сарын хурлын тэмдэглэл → «Дугаар 08»
         · баазын цорын ганц бичлэг → «26/10 ХАБ»
       Иймд: ОН(2 орон)/САР(2 орон), шалгалтын огноогоор.

       ⚠ ЧӨЛӨӨТ ТЕКСТ ХЭВЭЭР ҮЛДЭЭВ. Нэгж бүр өөрийн цувралтай байж
         болно (ж: «ХАБ» дагавар) тул санал болгосныг дарж бичнэ.
         Хатуу хэлбэр тулгавал байгаа цувралууд тасарна. */
    const _md = insp?.inspection_date ? moment(insp.inspection_date) : moment();
    const suggestedNo = _md.format("YY/MM");
    const f = { meeting_no: suggestedNo, override: "", reason: "" };
    Modal.confirm({
      title: "Хурлаар батлах (эцсийн дүн)",
      width: 620,
      content: (
        <div style={{ fontSize: 12.5 }}>
          <p style={{ marginBottom: 10 }}>
            ХЭМАБМТ-06 4.1.4.3 — комиссын гишүүдийн оноог нэгтгэн дундажлан,
            <b> комиссын дарга</b> үнэлгээг тогтооно. Батласнаар шалгалт
            хаагдана.
          </p>
          <div style={{ marginBottom: 8 }}>
            <div style={{ color: "#8A94A6", marginBottom: 2 }}>
              Хурлын протоколын дугаар
            </div>
            <Input
              size="small"
              defaultValue={suggestedNo}
              placeholder={suggestedNo}
              onChange={(e) => (f.meeting_no = e.target.value)}
            />
            <div style={{ fontSize: 11, color: "#8A94A6", marginTop: 3 }}>
              Загвар: <b>ОН/САР</b> — шалгалтын огноогоор{" "}
              <b>{suggestedNo}</b>. Нэгж өөрийн цувралтай бол дарж бичнэ
              (ж: <i>{suggestedNo} ХАБ</i>). Энэ дугаар хурлын тэмдэглэл
              болон акт албан шаардлага дээр хэвлэгдэнэ.
            </div>
          </div>
          {/* 4.1.4.5 — тоон дүн хангалттай байсан ч давтагдсан, ноцтой
              зөрчлийн улмаас «хангалтгүй» гэж өгч БОЛНО. Үндэслэл ЗААВАЛ. */}
          <div style={{ marginBottom: 8 }}>
            <div style={{ color: "#8A94A6", marginBottom: 2 }}>
              Үнэлгээг гараар өөрчлөх (4.1.4.5) — заавал биш
            </div>
            <Select
              size="small"
              allowClear
              style={{ width: "100%" }}
              placeholder="Автомат бодолтоор"
              onChange={(v) => (f.override = v || "")}
              options={[
                { value: "good", label: "Сайн" },
                { value: "ok", label: "Хангалттай" },
                { value: "bad", label: "Хангалтгүй" },
              ]}
            />
          </div>
          <div>
            <div style={{ color: "#8A94A6", marginBottom: 2 }}>
              Үндэслэл (гараар өөрчилбөл ЗААВАЛ)
            </div>
            <Input.TextArea
              size="small"
              autoSize={{ minRows: 2, maxRows: 4 }}
              placeholder="жишээ: урьд шалгалтын зөрчил давтагдан арилаагүй"
              onChange={(e) => (f.reason = e.target.value)}
            />
          </div>
        </div>
      ),
      okText: "Батлах",
      cancelText: "Болих",
      onOk: async () => {
        try {
          const res = await xabInspectionApi.approveMeeting(id, {
            meeting_no: f.meeting_no || undefined,
            eval_override: f.override || undefined,
            eval_override_reason: f.reason || undefined,
          });
          const m = EVAL_META[res.evaluation];
          message.success(
            `Хурлаар батлагдлаа. Үнэлгээ: ${m ? m.label : "—"}` +
              (res.score_percent != null ? ` (${res.score_percent}%)` : ""),
          );
          onClosed?.(res);
          load();
        } catch (e) {
          // 400 — үндэслэлгүй override · 403 — дарга биш · 409 — илгээгээгүй
          message.error(e?.response?.data?.message || "Батлахад алдаа гарлаа");
          throw e; // Modal онгойлтой үлдэнэ — бөглөсөн зүйл алдагдахгүй
        }
      },
    });
  };

  // ── Албан шаардлага үүсгэх (ХЭМАБМТ-06 2.5.5) ────────────────────────
  // Шалгагч мөр бүрд зөрчлөө аль хэдийн бичсэн байдаг тул дахин гараар
  // бичихгүй — бүх зөрчлийг цуглуулж баримт болгоно.
  /* ── Албан шаардлага үүсгэх (ХЭМАБМТ-06 2.5.5) ──────────────────
     ⚠ 2026-10-05: урьд нь `Modal.confirm` («Үүсгэх үү?») байсан тул
     баримт нь ХАЯГГҮЙ, толгой догол мөргүй, хугацаагүй үүсч, ХАБЭАХ
     дараа нь албан шаардлагын хуудаснаас бүгдийг гараар нөхдөг байв.
     Одоо МАЯГТ нээгдэж, цех/хэсэг/огноо/зөрчлийн тоо нь шалгалтаас
     ШУУД бөглөгдөж, журмын заавар нь лавлахаас гарна. */
  const doFormalRequirement = () => {
    const findings = (items || []).filter((i) =>
      String(i.finding_text || "").trim(),
    );
    if (!findings.length) {
      message.warning(
        "Зөрчил бүртгэгдээгүй тул албан шаардлага гаргах шаардлагагүй",
      );
      return;
    }
    setFrOpen(true);
  };

  /** Үүссэний дараа — хуудсыг «үүссэн · харах» төлөвт ШУУД хөрвүүлнэ. */
  const onFormalReqCreated = (res) => {
    /* ⚠ `load()`-ийг ДУУДАХГҮЙ: тэр нь `setItems(...)`-аар бүх мөрийг
       серверийн хувилбараар дарж, зөрчлийн цонхонд бөглөж байсан
       тайлбар, хариуцагч, хугацаа БҮГД арилна (2026-09-30-ны сургамж
       — `reloadFiles`-ийн тайлбарыг үз). `formal_req`-ийг л НЭМНЭ. */
    const it = res?.item || {};
    setInsp((prev) =>
      prev
        ? {
            ...prev,
            formal_req: {
              id: it.id,
              code: it.code,
              doc_type: it.doc_type,
              requirement_no: it.requirement_no,
              status: it.status,
              title: it.title,
              issue_date: it.issue_date,
              due_date: it.due_date,
              issued_by_name: it.issued_by_name,
              can_edit: (it.status || "") !== "closed",
            },
          }
        : prev,
    );
    message.success({
      content:
        (res?.message || "Албан шаардлага үүслээ") +
        (it.requirement_no || it.code
          ? ` — ${it.requirement_no || it.code}. «Харах · засах» товчоор нээнэ.`
          : ""),
      duration: 6,
    });
  };

  const doClose = () => {
    const issues = closeIssues();
    if (!issues.length) {
      Modal.confirm({
        title: "Шалгалтыг дүгнэж хаах уу?",
        width: 520,
        content: (
          <div>
            <p>Оноо/үнэлгээ автоматаар бодогдоно. Хаасны дараа засах боломжгүй.</p>
            {/* Хавсралт №4-ийн доод хэсэг: САНАЛ (сахилгын шийтгэл, сайшааж
                урамшуулах). Өмнө нь маягтад огт байгаагүй — үнэлгээ бодогдоод
                л зогсдог байв. */}
            {insp?.stage_code === "III" ? (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                  САНАЛ (хавсралт №4)
                </div>
                <Input.TextArea
                  rows={2}
                  placeholder="Сахилгын шийтгэл ногдуулах санал (байвал)"
                  style={{ marginBottom: 6 }}
                  onChange={(e) => (proposalRef.current.discipline = e.target.value)}
                />
                <Input.TextArea
                  rows={2}
                  placeholder="Сайшааж урамшуулах санал (байвал)"
                  onChange={(e) => (proposalRef.current.reward = e.target.value)}
                />
              </div>
            ) : null}
          </div>
        ),
        okText: "Хаах",
        cancelText: "Болих",
        onOk: () => reallyClose(false),
      });
      return;
    }
    Modal.confirm({
      title: "Дутуу бөглөсөн байна",
      width: 480,
      content: (
        <div>
          <p style={{ marginBottom: 8 }}>
            Журмаар зөрчил бүрт <b>арилгах хугацаа</b> ба <b>хариуцагч</b> заавал
            байх ёстой. Одоогийн байдлаар:
          </p>
          <ul style={{ paddingLeft: 18, margin: 0, color: "#B91C1C" }}>
            {issues.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        </div>
      ),
      okText: "Ингээд хаах",
      okButtonProps: { danger: true },
      cancelText: "Буцаж засах",
      onOk: () => reallyClose(false),
    });
  };

  // Бүлгээр бүлэглэх
  const groups = useMemo(() => {
    const g = [];
    const byName = {};
    items
      /* ── БУСАД ГИШҮҮНИЙ МӨРИЙГ НУУНА (2026-11-05) ─────────────────
         Хэрэглэгчийн шийдвэр: гишүүн бусдын ОНООГ харахгүй, зөвхөн
         ДУНДАЖ ба «хэн үнэлсэн» нь харагдана. Бусдын мөрийг хуудсанд
         үлдээвэл нэг үзүүлэлт 2-6 мөр болж, оноо нь ч ил гарна.
         ⚠ КОМИССЫН УДИРДАГЧ · ХАБЭА (`own_groups_only` биш) бол БҮГДИЙГ
           харна — тэд хяналт тавьдаг.
         ⚠ «Бусдын бүлгийг харах» (`showOthers`) асаасан үед ч бүгд
           гарна: тэр нь зориудаар хяналтын харагдац. */
      .filter(
        (it) =>
          !perMember ||
          !ownGroupsOnly ||
          showOthers ||
          it.mine !== false,
      )
      .forEach((it) => {
        const key = it.group_name || "Үзүүлэлтүүд";
        if (!byName[key]) {
          byName[key] = { name: key, rows: [] };
          g.push(byName[key]);
        }
        byName[key].rows.push(it);
      });
    return g;
  }, [items, perMember, ownGroupsOnly, showOthers]);

  /* ── ХУУДАСНЫ ДЭС ДУГААР (2026-10-26) ──────────────────────────────
     Хэрэглэгч: «лавлах дээр үүссэн дугаарыг цаанаа нууж, ил харуулах нь
     энгийн хуудасны үзүүлэлтийг дугаарласан харуулах».

     ⚠ ЯАГААД: `item_no` нь ЛАВЛАХ дээр БҮЛЭГ ТУС БҮРД 1-ЭЭС ДАХИН
       ЭХЭЛДЭГ. Хэмжсэн (template 1): «Ажилтнуудад тавигдах ХЭМАБ-ын
       шаардлага» нь 1-11, дараагийн «Ажлын байранд тавигдах ХАБ-ын
       шаардлага» нь ДАХИАД 1, 2, 3… Нэг хуудсан дотор давхардсан
       (template, item_no) хос — 6. Иймд «д/д» багана 1…11, 1, 2, 3 гэж
       харагдаж, хэрэглэгч «11 дүгээр» гэж хэлэхэд аль нь болох нь
       мэдэгдэхгүй байв.

     ⚠ `item_no`-Г ХАДГАЛСАН ЧИГЭЭР ҮЛДЭЭНЭ — зөвхөн ХАРУУЛАХ дугаарыг
       сольж байна. Тэр талбарыг зөрчил/аюулын холбоос ашигладаг
       (`prework_item_no`, AssignmentPanel, InspectionLinkBlock,
       серверийн `unresolved_findings`) тул утгыг солих нь тэдгээрийг
       эвдэнэ.

     ⚠ БҮХ `items`-ээр дугаарлана, зөвхөн ХАРАГДАЖ байгаагаар биш:
       бүлгийн шүүлт (`groupVisible`) асаахад мөрийн дугаар ҮСРЭХГҮЙ
       байх ёстой — нэг үзүүлэлт үргэлж нэг дугаартай. */
  const seqNo = useMemo(() => {
    const m = {};
    // ⚠ ИЖИЛ ЗААЛТЫН мөрүүд ИЖИЛ дугаартай (2026-10-26): нэг
    //   үзүүлэлтэд хэд хэдэн зөрчил бүртгэх боломж нэмэгдсэн тул
    //   мөр тутамд шинэ дугаар өгвөл нэг заалт хоёр ӨӨР үзүүлэлт мэт
    //   харагдана. Дээрх «нэг үзүүлэлт үргэлж нэг дугаартай» зарчим
    //   хэвээр барина.
    // ⚠ `template_item_id IS NULL` (чөлөөт зөрчил) бүр ӨӨРИЙН дугаартай —
    //   тэдгээр нь хуудасны үзүүлэлтэд харьяалагдахгүй, тус бүр бие даасан.
    const byItem = new Map();
    let n = 0;
    items.forEach((it) => {
      const tid = it.template_item_id;
      if (tid != null && byItem.has(tid)) {
        m[it.id] = byItem.get(tid);
        return;
      }
      n += 1;
      if (tid != null) byItem.set(tid, n);
      m[it.id] = n;
    });
    return m;
  }, [items]);

  /** НЭГ ҮЗҮҮЛЭЛТЭД ХЭД ДЭХ ЗӨРЧИЛ ВЭ — {row.id: [дугаар, бүгд]}.
   *
   * Хэрэглэгч (2026-10-30): «нэг үзүүлэлт дээр хэд хэдэн зөрчил
   * бүртгэх боломжтой бх, тус бүрийг засах харах боломжтой».
   *
   * ⚠ ЯАГААД ХЭРЭГТЭЙ ВЭ: давхар мөрүүд нь ИЖИЛ дугаар, ИЖИЛ
   *   `item_text`-тэй (дээрх `seqNo`) тул хоёр зөрчил нь дэлгэц дээр
   *   ЯГ АДИЛХАН харагдана — хэрэглэгч аль нь алийг засаж байгаагаа
   *   мэдэхгүй. Тиймээс 2-оос дээш үед «Зөрчил 1/2» гэж тэмдэглэнэ.
   * ⚠ ЗӨВХӨН 2-оос дээш үед: нэг зөрчилтэй 23 000 мөрд шошго
   *   нэмбэл хуудас шуугиантай болно. */
  const findingIdx = useMemo(() => {
    const cnt = new Map();
    items.forEach((it) => {
      const tid = it.template_item_id;
      if (tid == null) return;
      cnt.set(tid, (cnt.get(tid) || 0) + 1);
    });
    const seen = new Map();
    const m = {};
    items.forEach((it) => {
      const tid = it.template_item_id;
      if (tid == null || (cnt.get(tid) || 0) < 2) return;
      const k = (seen.get(tid) || 0) + 1;
      seen.set(tid, k);
      m[it.id] = [k, cnt.get(tid)];
    });
    return m;
  }, [items]);

  const stats = useMemo(() => {
    const answered = items.filter((i) =>
      scoreMode ? i.score != null : i.answer === "yes" || i.answer === "no"
    );
    const scoreSum = items.reduce((s, i) => s + (i.score || 0), 0);
    return {
      total: items.length,
      answered: answered.length,
      yes: items.filter((i) => i.answer === "yes").length,
      no: items.filter((i) => i.answer === "no").length,
      findings: items.filter(isFinding).length,
      overdue: items.filter(isOverdue).length,
      scoreSum,
      // Урьдчилсан хувь — хаахад backend мөн ийм томьёогоор бодно
      scorePct: answered.length
        ? Math.round((scoreSum * 100) / (scoreMax * answered.length))
        : null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, scoreMode]);

  if (loading)
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Spin />
      </div>
    );
  if (!insp) return <Empty description="Шалгалт олдсонгүй" />;

  return (
    <div className="icf-root" style={{ padding: 16, maxWidth: 1100, margin: "0 auto" }}>
      {/* ⚠ ҮЙЛДЛИЙН МӨР НААЛДАЦТАЙ (2026-09-24): 20-30 үзүүлэлт бөглөх үед
          хуудас урт тул «Хадгалах / Хаах / Хэвлэх» товч дээр гарахын тулд
          бүхэл хуудсыг эргүүлж гүйлгэх шаардлагатай байв. Одоо дээд талдаа
          наалдана — доош гүйхэд ч товчнууд харагдана.
          Утсанд толгой ба товчнууд эвхэгдэнэ (gutter-аар зайтай). */}
      <Row
        justify="space-between"
        align="middle"
        gutter={[8, 8]}
        className="icf-actionbar"
      >
        <Col>
          <Space wrap>
            {onBack && <Button onClick={onBack}>← Буцах</Button>}
            <h3 style={{ margin: 0 }}>{insp.code}</h3>
            <PageHelp topic="inspection/fill" />
            <Tag color={insp.stage_color || "blue"}>
              {insp.stage_name || insp.stage_code}
            </Tag>
            {closed ? (
              EVAL_META[insp.evaluation] ? (
                <Tag color={EVAL_META[insp.evaluation].color}>
                  {EVAL_META[insp.evaluation].label}
                </Tag>
              ) : (
                <Tag color="green">Хаагдсан</Tag>
              )
            ) : (
              <Tag color="blue">Нээлттэй</Tag>
            )}
          </Space>
        </Col>
        <Col>
          <Space>
            {/* ⚠ ХЭВЛЭХ — ШИНЭ ТАБ-Д нээнэ.
                Энэ компонент нь дэлгэрэнгүй ХУУДАС дээр ч, аюулын
                бүртгэлийн МОДАЛ дотор ч ажилладаг. Тухайн цонхондоо
                шилжвэл модал хаагдаж, бөглөж байсан зүйл алдагдана. */}
            <Tooltip title="Бөглөсөн хуудсыг журмын маягтаар хэвлэх — зөрчлийн зураг, гарын үсгийн мөр хамт">
              <Button
                onClick={() =>
                  // Шинэ таб-д түүх байхгүй тул «Буцах» хаашаа очихыг
                  // `from`-оор дамжуулна: дэлгэрэнгүй хуудас бол түүний
                  // өөрийн `state.from` (жагсаалт), эс бөгөөс одоогийн зам.
                  window.open(
                    `/inspection/xab/print/${insp.id}?from=${encodeURIComponent(
                      (window.history.state?.usr?.from) || window.location.pathname,
                    )}`,
                    "_blank",
                    "noopener",
                  )
                }
              >
                <i className="fas fa-print" style={{ marginRight: 6 }} />
                Хэвлэх
              </Button>
            </Tooltip>
            <Button
              type="primary"
              loading={saving}
              disabled={closed}
              onClick={() => saveWithHint()}
            >
              Хадгалах
            </Button>
            {/* Баталгаажуулалтыг doClose дотор Modal-аар хийнэ (дутуу бөглөлт шалгана) */}
            {/* Комиссын гишүүн хаахгүй — удирдагч л дүгнэнэ (2.5.1) */}
            {needMeeting ? (
              /* ХУРАЛ ШААРДАХ ШАТ (III · IV) — хоёр шаттай.
                 ⚠ «Дүгнэж хаах» ЭНД ГАРАХГҮЙ: backend тэр замыг 409-ээр
                   хаасан тул товч гаргавал дарж болохгүй товч болно. */
              meetState === "submitted" ? (
                /* ⚠ ТӨЛӨВИЙГ ХАРАГДУУЛНА + БУЦААХ АРГА ӨГНӨ.
                     Урьд нь товч нь зүгээр «Хурлаар батлах» болж хувирдаг
                     тул шалгагч «яагаад илгээх товч алга болов» гэж
                     эргэлзэж, санамсаргүй дарсан бол гацдаг байв
                     (2026-10-01, хэрэглэгч). */
                <>
                  <Tooltip
                    title={
                      "Хуралд илгээгдсэн" +
                      (insp?.submitted_at
                        ? " · " + moment(insp.submitted_at).format("MM-DD HH:mm")
                        : "")
                    }
                  >
                    <Tag color="blue" style={{ marginRight: 0 }}>
                      <i
                        className="fas fa-paper-plane"
                        style={{ marginRight: 5 }}
                      />
                      Хуралд илгээгдсэн
                    </Tag>
                  </Tooltip>
                  <Button size="small" type="link" onClick={doWithdrawMeeting}>
                    Буцаах
                  </Button>
                  <Tooltip title="Үнэлгээг комиссын дарга тогтооно (4.1.4.3)">
                    <Button
                      type="primary"
                      danger
                      disabled={closed}
                      onClick={doApproveMeeting}
                    >
                      <i className="fas fa-gavel" style={{ marginRight: 6 }} />
                      Хурлаар батлах
                    </Button>
                  </Tooltip>
                </>
              ) : (
                <Tooltip
                  title={
                    /* ⚠⚠ ГИШҮҮН ИЛГЭЭЖ ЧАДАХГҮЙ байв (2026-11-05).
                       Товч нь `restricted`-ээр хаагддаг — тэр нь
                       ердийн комиссын гишүүнд ҮРГЭЛЖ ҮНЭН. Гэтэл
                       хэрэглэгчийн журам: «үнэлгээ хийх хүн бүр …
                       үнэлгээгээ болсон гэж үзсэн үед дүнгиын хуралд
                       илгээнэ». Гишүүний илгээлт нь ШАЛГАЛТЫГ хуралд
                       явуулдаггүй — зөвхөн ӨӨРИЙН үнэлгээг
                       баталгаажуулдаг тул хаах шалтгаан байхгүй. */
                    memberSubmit
                      ? "Өөрийн үнэлгээгээ илгээнэ — бусад гишүүдийн "
                        + "үнэлгээтэй дундажлагдана"
                      : restricted
                        ? "Үр дүнг комиссын удирдагч хуралд илгээнэ"
                        : "Хаалтын хурлаас 5 цагийн өмнө илгээнэ (4.1.4.3)"
                  }
                >
                  <Button
                    disabled={closed || (restricted && !memberSubmit)}
                    onClick={doSubmitMeeting}
                  >
                    <i className="fas fa-paper-plane" style={{ marginRight: 6 }} />
                    {memberSubmit
                      ? "Үнэлгээгээ илгээх"
                      : "Дүнгийн хуралд илгээх"}
                  </Button>
                </Tooltip>
              )
            ) : ["I", "II"].includes(insp?.stage_code) ? (
              /* ⚠⚠ I·II ШАТАНД «ДҮГНЭЖ ХААХ» ТОВЧ ГАРАХГҮЙ
                 (хэрэглэгч 2026-11-01: «хяналт шалгалтын 1-2 р
                 төвшинд дүгнэж хаах гэсэн товч харуулахгүй байя, энэ
                 нь 2 БН дээр 3 шатанд багын нэгдсэн үнэлгээ гаргах,
                 4 шатанд нийт БН-ийн шалгалтын үнэлгээ бодох үед
                 ашиглагдана»).

                 ЯАГААД: ДҮГНЭЛТ нь нэгдсэн ҮНЭЛГЭЭ тогтоох үйлдэл —
                 III шатад БН-ийн комиссын нэгдсэн үнэлгээ, IV шатад
                 үйлдвэрийн хэмжээний үнэлгээ. I·II шат нь өдөр тутмын
                 (наряд · ажлын байр) хяналт тул дүгнэх зүйл байхгүй.

                 ⚠ ХАДГАЛАХ товч ХЭВЭЭР: шалгалт нь бүртгэл хэвээр
                   үлдэж, зөрчлүүд нь хариуцагчдаа хуваарилагдана.
                 ⚠ Сервер талд хориг ТАВИАГҮЙ: ХАБЭА/хөгжүүлэгч
                   шаардлагатай бол API-аар хааж чадна (хуучин I·II
                   шатны хаагдсан бүртгэлийг ч хөндөөгүй). */
              null
            ) : (
              <Tooltip title={restricted ? "Шалгалтыг комиссын удирдагч хаана" : ""}>
                <Button disabled={closed || restricted} onClick={doClose}>
                  Дүгнэж хаах
                </Button>
              </Tooltip>
            )}
            {/* Албан шаардлага — III, IV шат ба ээлжит бус хяналтын ГАРАЛТ
                (ХЭМАБМТ-06 2.5.5). Шатны лавлахад `produces_formal_req`
                тэмдэглэгдсэн үед л гарна — журмаар заагаагүй шатанд
                товч харагдахгүй. */}
            {/* ⚠ ҮҮССЭН БАРИМТЫГ МЭДЭГДЭНЭ (2026-10-05, хэрэглэгч:
                «албан шаардлага эсвэл акт үүссэн хуудас байхад
                мэдэгдэхгүй, дахин дарах тохиолдол гарч байна»).
                Backend нь давхардлыг хориглодог тул дата эвдрээгүй ч
                хэрэглэгч товч дарж, алдааны мессеж аваад төөрөгддөг
                байв. Товчийг зүгээр нуух нь ХАНГАЛТГҮЙ — үүссэн
                баримтыг ХАРАХ ба ЗАСАХ хаалга хэрэгтэй. */}
            {insp.stage_formal_req && insp.formal_req ? (
              <Space size={4}>
                <Tooltip
                  title={
                    `${insp.formal_req.doc_type === "act" ? "Акт" : "Албан шаардлага"}` +
                    ` аль хэдийн үүссэн · ${insp.formal_req.status || ""}` +
                    (insp.formal_req.issue_date
                      ? ` · ${insp.formal_req.issue_date}`
                      : "") +
                    (insp.formal_req.issued_by_name
                      ? ` · ${insp.formal_req.issued_by_name}`
                      : "")
                  }
                >
                  <Tag
                    color="volcano"
                    style={{ margin: 0, fontWeight: 700, cursor: "help" }}
                  >
                    <i
                      className="fas fa-file-contract"
                      style={{ marginRight: 5 }}
                    />
                    {insp.formal_req.requirement_no
                      ? `№${insp.formal_req.requirement_no}`
                      : insp.formal_req.code}
                  </Tag>
                </Tooltip>
                <Button
                  size="small"
                  onClick={() =>
                    window.open(
                      `/inspection/requirements?open=${insp.formal_req.id}` +
                        `&code=${encodeURIComponent(insp.formal_req.code)}`,
                      "_blank",
                    )
                  }
                >
                  {/* ⚠ ЗАСАХ эрх нь ТӨЛӨВӨӨС шалтгаална: биелэлт хүлээн
                      авч хаасан баримтыг засах нь аудитыг эвдэнэ
                      (backend ч хориглоно). */}
                  {insp.formal_req.can_edit ? "Харах · засах" : "Харах"}
                </Button>
              </Space>
            ) : insp.stage_formal_req ? (
              <Button
                type="primary"
                ghost
                onClick={doFormalRequirement}
              >
                <i className="fas fa-file-contract" style={{ marginRight: 6 }} />
                Албан шаардлага
              </Button>
            ) : null}
          </Space>
        </Col>
      </Row>

      {/* АЛБАН ШААРДЛАГА ҮҮСГЭХ МАЯГТ — журмын заавар, хаяглалт,
          «Иймд шаардах нь» загвар (2026-10-05) */}
      <FormalReqCreateModal
        open={frOpen}
        onClose={() => setFrOpen(false)}
        insp={insp}
        findingCount={
          (items || []).filter((i) => String(i.finding_text || "").trim())
            .length
        }
        onCreated={onFormalReqCreated}
      />

      {/* ⚠ ЖИЖИГ БАЙДАЛД (2026-09-24): энэ хэсэг нь 3 мөр эзэлж, бөглөх
          хүснэгтийг доош түлхдэг байв. Мэдээлэл нь лавлагаа шинжтэй тул
          Alert-ийн оронд ХӨНГӨН мөр болгов (icon-гүй, жижиг фонт, багассан
          зай). Агуулга нь бүрэн хэвээр — зөвхөн харагдац жижгэрсэн. */}
      {insp.stage_code && (
        <Alert
          type="info"
          showIcon={false}
          banner
          style={{
            marginBottom: 8,
            padding: "4px 10px",
            fontSize: 11.5,
            lineHeight: 1.35,
            borderRadius: 6,
          }}
          message={
            <span style={{ fontSize: 11.5, color: "#3A4356" }}>
              <b>{insp.tseh_name || insp.bn_name || "—"}</b>
              {insp.tseh_name && insp.bn_name ? ` · ${insp.bn_name}` : ""}
              {insp.area_location ? ` · ${insp.area_location}` : ""} ·{" "}
              {insp.inspection_date
                ? moment(insp.inspection_date).format("YYYY-MM-DD")
                : ""}
              {insp.inspector_name ? ` · Шалгагч: ${insp.inspector_name}` : ""}
            </span>
          }
          description={
            <>
              <div style={{ fontSize: 11.5, color: "#5F6B7A" }}>
                Бөглөсөн:{" "}
                <b>
                  {stats.answered}/{stats.total}
                </b>
                {scoreMode
                  ? ` · Оноо ${stats.scoreSum}/${scoreMax * stats.answered}` +
                    (stats.scorePct != null ? ` (${stats.scorePct}%)` : "")
                  : ` · Тийм ${stats.yes} · Үгүй ${stats.no}`}
                {stats.findings > 0 ? ` · Зөрчил ${stats.findings}` : ""}
              </div>
              {/* Журмын шатны дүрэм — хэн, ямар давтамжтай, аль хавсралтаар */}
              {(insp.stage_actor_label || insp.stage_frequency_label) && (
                <div style={{ marginTop: 2, fontSize: 10.5, color: "#8A94A6" }}>
                  {insp.stage_appendix ? `Хавсралт №${insp.stage_appendix} · ` : ""}
                  {insp.stage_frequency_label
                    ? `${insp.stage_frequency_label} · `
                    : ""}
                  {insp.stage_actor_label}
                </div>
              )}
            </>
          }
        />
      )}

      {/* III шат — БН-ийн комисс. Бусад шатанд гарахгүй: I/II нь нэг
          хүн/хэсгийн комисс, IV нь мэргэжилтэн тус бүр ӨӨРИЙН хуудастай
          (тэнд бүлэг хуваах шаардлагагүй). */}
      {insp.stage_code === "III" && insp.commission ? (
        <CommissionPanel inspection={insp} commission={insp.commission} onChanged={load} />
      ) : null}

      {/* ⚠ ТАНД БҮЛЭГ ОНООГООГҮЙ (2026-10-15): `own_groups_only` гишүүнд
          бүлэг оноогоогүй бол бүлэгтэй БҮХ мөр түгжигдэж, тэр хүн
          ЮУ Ч бөглөж чадахгүй — гэтэл шалтгаан нь дэлгэцэнд
          хаана ч бичигддэггүй байв. Баазад 2026-10-15-нд цех 22-ын
          11 гишүүн бүгд ийм байсан (лавлахад бүлэг оноогоогүй). */}
      {ownGroupsOnly && myGroupIds.size === 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 10 }}
          message="Танд хариуцах чиглэл (бүлэг) оноогоогүй байна"
          description={
            <span style={{ fontSize: 12 }}>
              Тиймээс үзүүлэлтүүд бөглөх боломжгүй байна. Комиссын
              удирдагч дээрх «Комисс» хэсгээс, эсвэл ХАБЭА-гийн лавлах →
              «III шатны комисс» дээр танд Хавсралт №4-ийн бүлгийг оноож
              өгөх шаардлагатай.
            </span>
          }
        />
      ) : null}

      {/* Комиссын гишүүн — өөрийн бүлэг л идэвхтэй; бусдынхыг хүсвэл харна */}
      {ownGroupsOnly ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 10,
            padding: "6px 10px",
            background: "#F0FDF4",
            border: "1px solid #BBF7D0",
            borderRadius: 6,
            fontSize: 12,
          }}
        >
          <i className="fas fa-user-check" style={{ color: "#0F6E56" }} />
          <span>
            Таны хариуцах бүлэг:{" "}
            <b>
              {(commission?.groups || [])
                .filter((g) => myGroupIds.has(Number(g.id)))
                .map((g) => g.name)
                .join(" · ") || "оноогдоогүй"}
            </b>
            {/* ⚠ ХЭДЭН мөр гэдгийг хэлнэ — «бөглөж дуусав уу» гэдгийг
                шалгахад хэрэгтэй (бүлгийн нэр нь тоог хэлэхгүй). */}
            {myItemCount ? (
              <span style={{ color: "#5B6472" }}>
                {" "}
                · {myItemCount} үзүүлэлт
              </span>
            ) : null}
            {/* ── ГИШҮҮДИЙН ИЛГЭЭЛТ (2026-11-05) ────────────────────
                Хэрэглэгч: «үнэлгээгээ болсон гэж үзсэн үед дүнгиын
                хуралд илгээнэ, нийт үнэлгээчдийн … дундажаар үнэлгээ
                гарна» — иймд «хэн илгээсэн, хэн хүлээгдэж байгаа» нь
                хуудсан дээр харагдах ёстой. Хуваарь нь БҮЛЭГ оноогдсон
                гишүүд (бүлэггүй хүн үнэлэх зүйлгүй). */}
            {(() => {
              const mem = (commission?.members || []).filter(
                (m) => (m.group_ids || []).length,
              );
              if (!mem.length) return null;
              const done = mem.filter((m) => m.submitted_at);
              const rest = mem.filter((m) => !m.submitted_at);
              return (
                <Tooltip
                  title={
                    <div style={{ fontSize: 12 }}>
                      <div>
                        Илгээсэн:{" "}
                        {done.map((m) => m.name || m.tn).join(", ") || "—"}
                      </div>
                      {rest.length ? (
                        <div style={{ marginTop: 4 }}>
                          Хүлээгдэж байна:{" "}
                          {rest.map((m) => m.name || m.tn).join(", ")}
                        </div>
                      ) : null}
                    </div>
                  }
                >
                  <Tag
                    color={rest.length ? "orange" : "green"}
                    style={{ marginLeft: 8, fontSize: 10.5 }}
                  >
                    Үнэлгээ илгээсэн {done.length}/{mem.length}
                  </Tag>
                </Tooltip>
              );
            })()}
          </span>
          <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
            <Switch size="small" checked={showOthers} onChange={setShowOthers} />
            Бусад гишүүний бүлгийг харах (зөвхөн харах)
          </span>
        </div>
      ) : null}

      {stats.overdue > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Арилгах хугацаа хэтэрсэн зөрчил: ${stats.overdue}`}
          description="Хугацаа хэтэрсэн мөрүүд улаанаар тэмдэглэгдсэн."
        />
      )}

      {/* Комисс — II шат хэсгийн, III шат БН-ийн комиссоор хийгдэнэ (журам) */}
      {Array.isArray(insp.commission) && insp.commission.length > 0 && (
        <Card
          size="small"
          title={`Комиссын бүрэлдэхүүн (${insp.commission.length})`}
          style={{ marginBottom: 12 }}
          headStyle={{ background: "#fafafa", fontWeight: 600 }}
        >
          <Space wrap size={8}>
            {insp.commission.map((c) => (
              <Tag
                key={c.id}
                color={c.member_role === "chair" ? "gold" : "default"}
              >
                {c.member_role === "chair" ? "Дарга: " : ""}
                {c.name || c.tn}
                {c.position_name ? ` — ${c.position_name}` : ""}
              </Tag>
            ))}
          </Space>
        </Card>
      )}

      {noRight && insp?.status !== "closed" && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Зөвхөн харах горим — энэ шалгалтыг бүртгэл оруулсан, шалгасан хүн эсвэл ХАБЭА хэлтэс засварлана."
        />
      )}
      {insp?.status === "closed" && (
        <Alert
          type={
            insp.evaluation === "bad"
              ? "error"
              : insp.evaluation === "good"
              ? "success"
              : "warning"
          }
          showIcon
          style={{ marginBottom: 12 }}
          message={`Дүн: ${
            EVAL_META[insp.evaluation]?.label || "Хаагдсан"
          }${insp.score_percent != null ? ` (${insp.score_percent}%)` : ""}`}
          /* ── ДАХИН НЭЭХ (2026-10-08) ─────────────────────────────────
             ⚠ Хаагдсаны дараа зөрчилд даалгавар хуваарилах, биелэлт
               засах бүгд хаагддаг (backend 403). Эрхтэй хүн ч
               БУЦААХ ТОВЧГҮЙ байсан тул мухардалд ордог байв.
             ⚠ `can_modify` нь ХААГДСАН шалгалтад ЗӨВХӨН ХАБЭА-д
               үнэн — тул энэ товч өөрөө эрхийн шүүлт болно. Клиент
               дүрмийг давтаж бичихгүй (сервер мөн 403 буцаана).
             ⚠ Шалтгаан ЗААВАЛ: хаагдсан шалгалт бол албан дүн,
               «хэн яагаад буцаасан» нь аудитад үлдэх ёстой. */
          /* ⚠ `can_reopen` нь СЕРВЕРИЙН шийдвэр (шатаас хамаарна:
             1-3 шатыг оруулсан хүн, IV шатыг зөвхөн ХАБЭА). Клиент
             дүрмийг давтаж бичвэл товч гараад дарахад 403 гарна. */
          action={
            insp.can_reopen ? (
              <Button
                size="small"
                danger
                /* ⚠ `saving` — энэ компонентынх. Файлд өөр компонентод
                   `busy` нэртэй төлөв бий, түүнийг хэрэглэвэл
                   `no-undef` болно. */
                loading={saving}
                onClick={doReopen}
                icon={<i className="fas fa-undo" />}
              >
                &nbsp;Дахин нээх
              </Button>
            ) : null
          }
        />
      )}

      {/* ─────────────────────────────────────────────────────────────
          Албан ёсны МАЯГТЫН хэлбэр (ChecklistBlank-тай ижил бүтэц):
            д/д | Үзүүлэлтүүд | Тийм | Үгүй | Тэмдэглэл
          IV шатны маягтад (eval_mode='percent') Тийм/Үгүйн оронд 0–5 оноо.
          Цаасан маягт дээр "Үгүй" мөрийн ТЭМДЭГЛЭЛ баганад илэрсэн зөрчлийг
          бичдэг тул тэр багана нь finding_text-тэй холбогдоно. Хугацаа,
          хариуцагч зэрэг дэлгэрэнгүй нь мөрийн доор задарна.
          ───────────────────────────────────────────────────────────── */}
      <style>{ICF_MOBILE_CSS}</style>
      {/* ── БҮГДИЙГ «ТИЙМ» ── олон мөртэй хуудсанд нэг бүрчлэн дарах
          төвөгтэй тул бөглөөгүйг нэг дор бөглөөд, асуудалтайг нь «Үгүй»
          болгож зөрчил бүртгэнэ. Бөглөөгүй мөр АЛГА бол товч ГАРАХГҮЙ —
          дарж болохгүй товч харуулах нь төөрөгдүүлнэ. */}
      {!closed && blankRows.length > 0 ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 6,
            flexWrap: "wrap",
          }}
        >
          <Button size="small" onClick={fillAllYes}>
            <i className="fas fa-check-double" style={{ marginRight: 6 }} />
            {bulkLabel}
            <span style={{ color: "#8A94A6", marginLeft: 6 }}>
              ({blankRows.length} бөглөөгүй)
            </span>
          </Button>
          <span style={{ fontSize: 11.5, color: "#8A94A6" }}>
            дараа нь асуудалтай мөрөө «Үгүй» болгоод зөрчлөө бүртгэнэ
          </span>
        </div>
      ) : null}
      <div style={FORM_WRAP} className="icf-wrap">
        <table style={FORM_TABLE} className="icf-table">
          <thead>
            <tr>
              <th style={{ ...FORM_TH, width: 44 }}>д/д</th>
              <th style={FORM_TH}>Үзүүлэлтүүд</th>
              {scoreMode ? (
                <th style={{ ...FORM_TH, width: 210 }}>
                  Оноо ({scoreMin}-{scoreMax})
                </th>
              ) : (
                <>
                  <th style={{ ...FORM_TH, width: 62 }}>Тийм</th>
                  <th style={{ ...FORM_TH, width: 62 }}>Үгүй</th>
                </>
              )}
              <th style={{ ...FORM_TH, width: 260 }}>Тэмдэглэл</th>
              {/* ── ЗӨРЧЛИЙН ТӨЛӨВ — ЖАГСААЛТЫН БАГАНА (2026-11-05) ──
                  Хэрэглэгч: «Зөрчлийн төлөв хэсгийг жагсаалтын нэг
                  багана болгон гарга». Өмнө нь зөрчлийн ЦОНХНЫ дотор
                  карт байсан тул 20 зөрчлийн төлөвийг харах/солихын
                  тулд цонх 20 удаа онгойлгох шаардлагатай байв. */}
              <th style={{ ...FORM_TH, width: 150 }}>Төлөв</th>
            </tr>
          </thead>
          <tbody>
            {groups.filter(groupVisible).map((grp) => {
              /* ⚠ ӨӨРИЙН ЧИГЛЭЛ ИЛТ ЯЛГАРНА (2026-10-15, хэрэглэгч):
                 «өөрийн нь шалгах үзүүлэлтүүд идэвхтэй харагддаг бх».
                 Өмнө нь өөрийн мөрүүд зөвхөн БӨГЛӨГДӨХ боломжтой
                 байсан — харагдацаар нь бусдынхаас ялгардаггүй тул
                 100 мөрт хаана өөрийн хэсэг байгааг хайх шаардлагатай
                 байв. Одоо бүлгийн толгой ба мөрүүд нь ногоон өнгөөр
                 тэмдэглэгдэнэ. */
              const grpGid = grp.rows[0]?.group_id;
              const grpMine =
                !!commission?.members?.length &&
                grpGid != null &&
                myGroupIds.has(Number(grpGid));
              return (
              <React.Fragment key={grp.name}>
                {groups.length > 1 && (
                  <tr className="icf-group">
                    <td
                      style={
                        grpMine
                          ? {
                              ...FORM_GROUP,
                              background: "#E8F7F0",
                              borderLeft: "3px solid #0F6E56",
                              color: "#0B5340",
                            }
                          : FORM_GROUP
                      }
                      colSpan={scoreMode ? 4 : 5}
                    >
                      {grp.name}
                      {/* Комиссын хариуцагч — журам 2.5.3 «гишүүн тус бүр» */}
                      {(() => {
                        const gid = grp.rows[0]?.group_id;
                        const owner = gid != null ? groupOwner[gid] : null;
                        const mine = gid != null && myGroupIds.has(Number(gid));
                        if (!commission?.members?.length) return null;
                        return (
                          <span style={{ marginLeft: 10, fontWeight: 500, fontSize: 11.5 }}>
                            {owner ? (
                              <Tag color={mine ? "green" : "default"} style={{ marginInlineEnd: 0 }}>
                                <i className="fas fa-user" style={{ marginRight: 4 }} />
                                {owner}
                                {mine ? " · та" : ""}
                              </Tag>
                            ) : (
                              <Tag style={{ marginInlineEnd: 0 }}>хариуцагч оноогоогүй</Tag>
                            )}
                            {ownGroupsOnly && !mine ? (
                              <span style={{ color: "#8A94A6", marginLeft: 6 }}>
                                <i className="fas fa-lock" /> зөвхөн харах
                              </span>
                            ) : null}
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                )}
                {grp.rows.map((row) => {
                  const over = isOverdue(row);
                  const finding = isFinding(row);
                  return (
                    <React.Fragment key={row.id}>
                      {/* ⚠ Хугацаа хэтэрсэн нь ЭРХЭМ (улаан) — өөрийн
                          чиглэлийн ногоон өнгө түүнийг дарж болохгүй. */}
                      <tr
                        style={
                          over
                            ? { background: "#FEF2F2" }
                            : grpMine
                              ? { background: "#F7FCFA" }
                              : undefined
                        }
                      >
                        <td style={{ ...FORM_TD, textAlign: "center" }} className="icf-no">
                          {/* Хуудасны дэс дугаар — `item_no` нь бүлэг
                              тус бүрд 1-ээс эхэлдэг тул ил харуулахгүй
                              (дээрх `seqNo`-ийн тайлбарыг үз). */}
                          {seqNo[row.id] ?? row.item_no}
                        </td>
                        <td style={FORM_TD} className="icf-text">
                          {row.item_text}
                          {/* НЭГ ҮЗҮҮЛЭЛТИЙН ХЭД ДЭХ ЗӨРЧИЛ — давхар
                              мөрүүд ижил дугаар, ижил бичвэртэй тул
                              үүнгүйгээр аль нь алийг мэдэх арга байхгүй
                              (`findingIdx`-ийн тайлбарыг үз). */}
                          {findingIdx[row.id] ? (
                            <Tag
                              color="volcano"
                              style={{ marginLeft: 6, fontSize: 10.5 }}
                            >
                              Зөрчил {findingIdx[row.id][0]}/
                              {findingIdx[row.id][1]}
                            </Tag>
                          ) : null}
                          {over && (
                            <Tag color="red" style={{ marginLeft: 6 }}>
                              Хугацаа хэтэрсэн
                            </Tag>
                          )}
                          {/* ── ХЭНИЙ БҮРТГЭЛ (2026-11-05) ─────────────
                              Удирдагч/ХАБЭА нь бүх гишүүний мөрийг
                              хардаг тул мөр ХЭНИЙХ болохыг хэлнэ. */}
                          {perMember && row.mine === false ? (
                            <Tag style={{ marginLeft: 6, fontSize: 10.5 }}>
                              <i
                                className="fas fa-user"
                                style={{ marginRight: 4 }}
                              />
                              {row.evaluator_name ||
                                memberNameByTn.get(
                                  String(row.evaluator_tn || ""),
                                ) ||
                                row.evaluator_tn}
                            </Tag>
                          ) : null}
                          {/* ── БҮЛГИЙН ДУНДАЖ ба ХЭН ҮНЭЛСЭН ──────────
                              Хэрэглэгч: «хүн бүр үнэлсэний дараа нэг
                              бүлэг дээр үнэлсэн үнэлгээг дундажлаж
                              тооцно».
                              ⚠ ЗӨВХӨН ДУНДАЖ ба НЭРС — бусдын ОНОО нь
                                харагдахгүй (2026-11-05-ны шийдвэр).
                              ⚠ Хуваарь нь тэр БҮЛЭГТ ОНООГДСОН гишүүдийн
                                тоо; бүлэггүй мөрд хуваарь үл мэдэгдэх
                                тул зөвхөн «N үнэлсэн» гэж харуулна. */}
                          {perMember && row.score_n > 0 ? (
                            <Tooltip
                              title={(() => {
                                const did = (row.score_tns || []).map(
                                  (t) =>
                                    memberNameByTn.get(String(t)) || String(t),
                                );
                                const gid =
                                  row.group_id == null
                                    ? null
                                    : Number(row.group_id);
                                const all = (commission?.members || [])
                                  .filter(
                                    (m) =>
                                      gid == null ||
                                      (m.group_ids || [])
                                        .map(Number)
                                        .includes(gid),
                                  )
                                  .map((m) => String(m.tn || ""));
                                const pending = all
                                  .filter(
                                    (t) =>
                                      t &&
                                      !(row.score_tns || [])
                                        .map(String)
                                        .includes(t),
                                  )
                                  .map(
                                    (t) => memberNameByTn.get(t) || t,
                                  );
                                return (
                                  <div style={{ fontSize: 12 }}>
                                    <div>Үнэлсэн: {did.join(", ") || "—"}</div>
                                    {pending.length ? (
                                      <div style={{ marginTop: 4 }}>
                                        Үнэлээгүй: {pending.join(", ")}
                                      </div>
                                    ) : null}
                                  </div>
                                );
                              })()}
                            >
                              <Tag
                                color="geekblue"
                                style={{ marginLeft: 6, fontSize: 10.5 }}
                              >
                                Дундаж {row.score_avg}
                                {" · "}
                                {row.score_n}
                                {row.group_id != null &&
                                groupMemberCount.get(Number(row.group_id))
                                  ? "/" +
                                    groupMemberCount.get(Number(row.group_id))
                                  : ""}{" "}
                                үнэлсэн
                              </Tag>
                            </Tooltip>
                          ) : null}
                          {/* Шалгах баримт бичиг — шалгагч ЮУГ үзэхээ мэдэх
                              ёстой (журмын хуудасны багана). */}
                          {row.evidence_doc && (
                            <div
                              style={{
                                marginTop: 3,
                                fontSize: 11.5,
                                color: "#5B6472",
                              }}
                            >
                              <i
                                className="fas fa-folder-open"
                                style={{ marginRight: 5, color: "#8A94A6" }}
                              />
                              {row.evidence_doc}
                            </div>
                          )}
                          {row.rule_ref && (
                            /* ⚠ ЗААЛТ ХОЛБОГДСОН бол НОГООН + бүтэн эхийг
                               tooltip-д: шалгагч «ЦХАБДД-ийн 14.3» гэснээс
                               ЮУ гэж заасныг мэдэхгүй байв. Өнгө ганцаараа
                               мэдээлэл дамжуулахгүй — tooltip-д бичвэрээр
                               ч хэлнэ. */
                            <Tooltip
                              title={
                                row.clause && row.clause.clause_id
                                  ? `${row.clause.regulation_name || ""} ${
                                      row.clause.clause_no || ""
                                    } — ${clauseBrief(row.clause.clause_text)}`
                                  : "Журмын лавлахын заалттай холбогдоогүй эшлэл"
                              }
                            >
                              <Tag
                                color={
                                  row.clause && row.clause.clause_id
                                    ? "green"
                                    : "blue"
                                }
                                style={{ marginTop: 3, fontSize: 10.5 }}
                              >
                                {row.rule_ref}
                              </Tag>
                            </Tooltip>
                          )}
                          {/* Зөрчлийн бөглөгдсөн талбарууд — chip хэлбэрээр */}
                          {finding && (
                            <FindingChips row={row} seriousList={seriousList} overdue={over} />
                          )}
                        </td>

                        {scoreMode ? (
                          <td style={{ ...FORM_TD, textAlign: "center" }} className="icf-score">
                            <Radio.Group
                              size="small"
                              buttonStyle="solid"
                              disabled={rowLocked(row)}
                              value={row.score}
                              onChange={(e) => onScore(row, e.target.value)}
                            >
                              {scoreOptions.map((n) => (
                                <Radio.Button key={n} value={n}>
                                  {n}
                                </Radio.Button>
                              ))}
                            </Radio.Group>
                            {/* ⚠ БУЦААХ (2026-10-15): `Radio.Group` нь
                                сонголтыг цэвэрлэх боломж өгдөггүй тул
                                санамсаргүй дарсан оноо мөнхөд үлдэж,
                                дүн худал болдог байв. Оноо тавигдсан
                                үед л гарна. */}
                            {row.score != null && !rowLocked(row) ? (
                              <Tooltip title="Дарсан оноог буцаах (цэвэрлэх)">
                                <Button
                                  type="text"
                                  size="small"
                                  className="icf-score-clear"
                                  onClick={() => clearRow(row)}
                                  style={{
                                    marginLeft: 4,
                                    padding: "0 4px",
                                    color: "#8A94A6",
                                  }}
                                  icon={<i className="fas fa-undo" />}
                                />
                              </Tooltip>
                            ) : null}
                          </td>
                        ) : (
                          <>
                            {["yes", "no"].map((k) => (
                              <td
                                key={k}
                                className="icf-ans"
                                onClick={() => !rowLocked(row) && onAnswer(row, k)}
                                style={{
                                  ...FORM_TD,
                                  textAlign: "center",
                                  cursor: rowLocked(row) ? "default" : "pointer",
                                  color: k === "yes" ? "#16A34A" : "#DC2626",
                                  background:
                                    row.answer === k
                                      ? k === "yes"
                                        ? "#E7F6EC"
                                        : "#FDECEC"
                                      : undefined,
                                }}
                              >
                                {row.answer === k ? (
                                  <span
                                    style={{
                                      fontSize: 16,
                                      fontWeight: 800,
                                      color:
                                        k === "yes" ? "#16A34A" : "#DC2626",
                                    }}
                                  >
                                    &#10003;
                                  </span>
                                ) : (
                                  <span className="icf-ans-dot" style={{ color: "#D0D5DD" }}>&middot;</span>
                                )}
                                {/* Утсанд толгой нуугддаг тул товчин дээр нэр нь */}
                                <span className="icf-ans-lbl">{k === "yes" ? "Тийм" : "Үгүй"}</span>
                              </td>
                            ))}
                          </>
                        )}

                        <td style={FORM_TD} className="icf-note">
                          <Input.TextArea
                            autoSize={{ minRows: 1, maxRows: 4 }}
                            bordered={false}
                            disabled={rowLocked(row)}
                            placeholder={finding ? "Илэрсэн зөрчил..." : ""}
                            value={row.finding_text || ""}
                            onChange={(e) =>
                              setItem(row.id, { finding_text: e.target.value })
                            }
                            style={{ padding: 0, fontSize: 12 }}
                          />
                        </td>

                        {/* ── ЗӨРЧЛИЙН ТӨЛӨВ (2026-11-05) ──────────────
                            ⚠ ЗӨВХӨН ЗӨРЧИЛТЭЙ мөрөнд: «Тийм» гэж
                              хариулсан мөрөнд арилгах зүйл байхгүй тул
                              төлөв нь утгагүй (цонхонд ч гардаггүй).
                            ⚠ `submitted` нь ГАРААР сонгогдохгүй —
                              зөвхөн хариуцагчийн илгээлтээр орно
                              (цонхны хувилбартай ИЖИЛ дүрэм; хоёр тал
                              зөрвөл нэг нь урхи болно).
                            ⚠ `draft`-ийг ЦОНХНЫ хувилбарын АДИЛ
                              сонгогдохоор үлдээв — зөөлт нь зан төлөв
                              өөрчлөх ёсгүй. (Тэр нь үнэндээ серверийн
                              тооцоо тул гараар тавих нь утгагүй;
                              хоригловол ТУСДАА шийдвэр болно.) */}
                        <td style={FORM_TD} className="icf-fix">
                          {finding ? (
                            <Select
                              size="small"
                              style={{ width: "100%" }}
                              disabled={rowLocked(row)}
                              value={row.fix_status || "open"}
                              onChange={(v) =>
                                setItem(row.id, { fix_status: v })
                              }
                              options={Object.entries(FIX_META).map(
                                ([k, v]) => ({
                                  value: k,
                                  label: v,
                                  // Зөвхөн хариуцагчийн илгээлтээр
                                  disabled: k === "submitted",
                                }),
                              )}
                            />
                          ) : (
                            <span style={{ color: "#C7CDD8" }}>&mdash;</span>
                          )}
                        </td>
                      </tr>

                      {/* Зөрчилтэй мөрийн дэлгэрэнгүй — цаасан маягтын
                          "ИЛЭРСЭН ЗӨРЧИЛ" хэсгийн багануудтай тохирно */}
                      {finding && (
                        <tr className="icf-finding">
                          <td
                            style={{
                              ...FORM_TD,
                              background: "#FFF7F6",
                              borderLeft: "3px solid #DC2626",
                            }}
                            colSpan={scoreMode ? 4 : 5}
                          >
                            {/* ХУРААНГУЙ — мөрөнд үлдэх ЦОРЫН ГАНЦ зүйл. */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 10,
                                flexWrap: "wrap",
                              }}
                            >
                              <div style={{ flex: "1 1 260px", minWidth: 0 }}>
                                <div
                                  style={{
                                    fontSize: 12,
                                    lineHeight: 1.35,
                                    color: row.finding_text
                                      ? "#1F2430"
                                      : "#B45309",
                                  }}
                                >
                                  {row.finding_text ||
                                    "Зөрчил бүртгээгүй байна"}
                                </div>
                                <div
                                  style={{
                                    fontSize: 10.5,
                                    color: "#8A94A6",
                                    marginTop: 2,
                                    display: "flex",
                                    gap: 8,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {row.violation_kind === "serious" ? (
                                    <span
                                      style={{
                                        color: "#B91C1C",
                                        fontWeight: 700,
                                      }}
                                    >
                                      Ноцтой
                                    </span>
                                  ) : row.violation_kind === "minor" ? (
                                    <span>Ноцтой бус</span>
                                  ) : null}
                                  {row.violator_name ? (
                                    <span>
                                      <i
                                        className="fas fa-user-slash"
                                        style={{ marginRight: 4 }}
                                      />
                                      {row.violator_name}
                                    </span>
                                  ) : null}
                                  {row.penalty_text ? (
                                    <span>{row.penalty_text}</span>
                                  ) : null}
                                  {row.responsible_name ? (
                                    <span>
                                      <i
                                        className="fas fa-user-check"
                                        style={{ marginRight: 4 }}
                                      />
                                      {row.responsible_name}
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                              <Button
                                size="small"
                                type={row.finding_text ? "default" : "primary"}
                                danger={!row.finding_text}
                                disabled={rowLocked(row)}
                                onClick={() => setFindingModal(row.id)}
                              >
                                <i
                                  className="fas fa-clipboard-list"
                                  style={{ marginRight: 6 }}
                                />
                                {row.finding_text
                                  ? "Засах"
                                  : "Зөрчил бүртгэх"}
                              </Button>
                              {/* ӨӨР ЗӨРЧИЛ — нэг заалтад хэд хэдэн
                                  зөрчил илрэх нь бодит тохиолдол.
                                  ⚠ Зөвхөн зөрчил БҮРТГЭГДСЭН мөрөнд
                                    гарна: хоосон мөрөн дээр «өөр
                                    зөрчил» гэдэг утгагүй бөгөөд
                                    хэрэглэгч эхнийхийг нь бөглөхийн
                                    оронд хоосон мөр үржүүлнэ. */}
                              {row.finding_text && !rowLocked(row) ? (
                                <Button
                                  size="small"
                                  style={{ marginLeft: 6 }}
                                  disabled={saving}
                                  onClick={() => addExtraFinding(row)}
                                >
                                  <i
                                    className="fas fa-plus"
                                    style={{ marginRight: 6 }}
                                  />
                                  Өөр зөрчил
                                </Button>
                              ) : null}
                              {/* УСТГАХ — зөвхөн ХООСОН БА ИЛҮҮДЭЛ мөр.
                                  Тухайн үзүүлэлтэд өөр мөр байхгүй бол
                                  товч ГАРАХГҮЙ: ганц мөрийг устгавал
                                  үзүүлэлт хуудаснаас алга болно. */}
                              {!row.finding_text &&
                              !rowLocked(row) &&
                              row.template_item_id != null &&
                              items.some(
                                (x) =>
                                  x.id !== row.id &&
                                  x.template_item_id === row.template_item_id,
                              ) ? (
                                <Popconfirm
                                  title="Энэ хоосон мөрийг устгах уу?"
                                  okText="Устгах"
                                  cancelText="Болих"
                                  onConfirm={() => removeExtraRow(row)}
                                >
                                  <Button
                                    size="small"
                                    danger
                                    style={{ marginLeft: 6 }}
                                    disabled={saving}
                                  >
                                    <i
                                      className="fas fa-trash"
                                      style={{ marginRight: 6 }}
                                    />
                                    Устгах
                                  </Button>
                                </Popconfirm>
                              ) : null}
                            </div>
                            {/* ЗӨРЧЛИЙН БҮРЭН МАЯГТ.
                                ⚠ Талбарууд нь БАЙРАНДАА үлдсэн — зөвхөн
                                  савлагаа нь өөрчлөгдсөн. Ингэснээр `row`,
                                  `setItem`, `rowLocked` бүгд хамрах
                                  хүрээндээ хэвээр үлдэж, дахин бичихээс
                                  үүдэх алдаа гарахгүй. */}
                            <Modal
                              open={findingModal === row.id}
                              onCancel={() => setFindingModal(null)}
                              /* ⚠ ДЭЛГЭЦЭНД БАГТААНА (2026-09-30).
                                   Өмнө нь маягт нь цонхны өндрөөс хэтэрч,
                                   хуудас бүхэлдээ гүйдэг байсан тул
                                   «Хадгалах» товч харагдахгүй байв. Одоо
                                   зөвхөн БИЕ гүйнэ — толгой ба товч
                                   ҮРГЭЛЖ харагдана.
                                 ⚠ `maxWidth: 96vw` — жижиг дэлгэц дээр
                                   1100px нь хажуу тийш гүйлгэлт үүсгэнэ. */
                              width={1100}
                              style={{ top: 16, maxWidth: "96vw" }}
                              bodyStyle={{
                                maxHeight: "calc(100vh - 170px)",
                                overflowY: "auto",
                                overflowX: "hidden",
                              }}
                              destroyOnClose
                              title={
                                <span style={{ fontSize: 14 }}>
                                  <i
                                    className="fas fa-exclamation-triangle"
                                    style={{
                                      marginRight: 8,
                                      color: "#DC2626",
                                    }}
                                  />
                                  Зөрчил бүртгэх
                                  <span
                                    style={{
                                      fontWeight: 400,
                                      color: "#8A94A6",
                                      marginLeft: 8,
                                      fontSize: 12,
                                    }}
                                  >
                                    {row.item_text || ""}
                                  </span>
                                </span>
                              }
                              footer={
                                /* ⚠ «Болсон» ГЭЖ БИЧИЖ БОЛОХГҮЙ (2026-09-30).
                                   Товч нь зөвхөн цонхыг хаадаг байсан тул
                                   хэрэглэгч «болсон» гэдгийг «хадгалагдсан»
                                   гэж уншиж, хадгалагдаагүй байхад цонхоо
                                   хаадаг байв. Одоо ЖИНХЭНЭ хадгалаад л
                                   хаана — хадгалалт унавал цонх ОНГОЙЛТОЙ
                                   үлдэж, бөглөсөн зүйл алдагдахгүй. */
                                <Button
                                  type="primary"
                                  loading={saving}
                                  onClick={async () => {
                                    /* ⚠ ДУТУУ БОЛ ЧИМЭЭГҮЙ ХАДГАЛАХГҮЙ
                                       (2026-10-30). Сервер нь дутуу
                                       зөрчлийг НООРОГ болгодог бөгөөд
                                       ноорог нь «арилаагүй» тул шалгалт
                                       ХААГДАХГҮЙ. Хэрэглэгчид тэр
                                       агшинд хэлэхгүй бол хэдэн арван
                                       ноорог хуримтлаад хаалтын үед л
                                       мэдэгдэнэ (ХЭМЖСЭН: 138 ноорог).
                                       ⚠ БЛОКЛОХГҮЙ: хэрэглэгчийн
                                         2026-10-08-ны дүрэм —
                                         хадгалахыг зогсоохгүй. Тиймээс
                                         СОНГОЛТ өгнө. */
                                    const miss = findingMissing(row);
                                    if (miss.length) {
                                      const go = await new Promise((res) => {
                                        Modal.confirm({
                                          title:
                                            "Заавал бөглөх талбар дутуу",
                                          content: (
                                            <span>
                                              Дутуу: <b>{miss.join(", ")}</b>.
                                              <br />
                                              Ингэж хадгалбал зөрчил{" "}
                                              <b>«Ноорог (дутуу)»</b> төлөвт
                                              орж, арилаагүйд тооцогдоно —
                                              шалгалтыг ХААХ боломжгүй болно.
                                            </span>
                                          ),
                                          okText: "Ноорогоор хадгалах",
                                          cancelText: "Буцаж бөглөх",
                                          onOk: () => res(true),
                                          onCancel: () => res(false),
                                        });
                                      });
                                      if (!go) return;
                                    }
                                    const ok = await doSave(false);
                                    if (ok) setFindingModal(null);
                                  }}
                                >
                                  <i
                                    className="fas fa-save"
                                    style={{ marginRight: 6 }}
                                  />
                                  Хадгалах
                                </Button>
                              }
                            >
                              <InspectionFindingForm
                                /* ДУТУУ ТАЛБАРЫН ЖИВЭЭ АНХААРУУЛГА —
                                   хадгалахаас ӨМНӨ харагдана. Зөвхөн
                                   хадгалах даралт дээр хэлвэл хэрэглэгч
                                   аль талбарыг хайхаа мэдэхгүй.
                                   ⚠ МАЯГТЫН ДОТОР дамжуулна (2026-11-01,
                                     хэрэглэгч: «Зөрчил юун дээр гарсан
                                     энэ хэсэг болон энэ мэдээллийн
                                     хэсэг 2-г 2 багана хэлбэрээр
                                     харуул») — тэнхлэгийн картын ХАЖУУД
                                     суух ёстой тул цонхны биед нь
                                     үлдээвэл хоёр баганыг хийх
                                     боломжгүй. */
                                topAlert={
                                  findingMissing(row).length ? (
                                    <Alert
                                      type="warning"
                                      showIcon
                                      message={
                                        <span style={{ fontSize: 12.5 }}>
                                          Заавал бөглөх талбар дутуу:{" "}
                                          <b>
                                            {findingMissing(row).join(", ")}
                                          </b>{" "}
                                          — ийм зөрчил «Ноорог (дутуу)»
                                          төлөвт хадгалагдаж, шалгалтыг
                                          хаах боломжгүй болно.
                                        </span>
                                      }
                                    />
                                  ) : null
                                }
                                row={row}
                                mode={violationFormMode}
                                disabled={rowLocked(row)}
                                categories={hazCategories}
                                severities={hazSeverities}
                                seriousList={seriousList}
                                workplaces={workplaces}
                                devices={devices}
                                onPickFile={onPickPhoto}
                                onPatch={(patch) => setItem(row.id, patch)}
                                /* ⚠ ЗУРАГ нь ТАЙЛБАРЫН ХАЖУУД (2026-10-01).
                                   Урьд нь `extraBottom`-оор маягтын хамгийн
                                   ДООР байсан тул зөрчлийн цонх хэт урт болж,
                                   тайлбар бичих үед зургаа харах боломжгүй
                                   байв. ⚠ Composer-ийн `photo` хэсгийг энд
                                   АШИГЛАХГҮЙ: тэр нь `form.photos`-д
                                   хуримтлуулдаг бол шалгалтын зураг нь
                                   `xab.inspection_file`-д мөрийн id-гаар
                                   хадгалагддаг — асаавал зураг чимээгүй
                                   алдагдана. */
                                descriptionSide={
                                  <FindingPhotos
                                      inspectionId={insp?.id}
                                      itemResultId={row.id}
                                      files={insp?.files || []}
                                      disabled={rowLocked(row)}
                                      /* ⚠ `load` БИШ, `reloadFiles`
                                         (2026-09-30). `load()` нь
                                         `setItems(...)`-аар бүх мөрийг
                                         СЕРВЕРИЙН хувилбараар дардаг тул
                                         зөрчлийн цонхонд бөглөж байсан
                                         тайлбар, зөрчил гаргагч, шийтгэл,
                                         арга хэмжээ, хугацаа БҮГД арилж,
                                         хуудас refresh болсон мэт
                                         харагддаг байв. Зурагт хэрэгтэй нь
                                         ЗӨВХӨН `insp.files`. */
                                      onChanged={reloadFiles}
                                  />
                                }
                                extraBottom={
                                  <>
                                    {/* ── ЗӨРЧЛИЙН ТӨЛӨВ ──────────────────
                                        ⚠⚠ СОНГОГЧ НЬ ЖАГСААЛТАД ЗӨӨГДСӨН
                                          (2026-11-05, хэрэглэгч: «Зөрчлийн
                                          төлөв хэсгийг жагсаалтын нэг
                                          багана болгон гарга») — `icf-fix`
                                          багана. Энд ЗӨВХӨН «Хаалтын
                                          тайлбар» үлдэв: тэр нь урт
                                          бичвэр тул жагсаалтын багананд
                                          таарахгүй.
                                        ⚠ ХОЁР ГАЗАР ДАХИН ТАВИХГҮЙ: нэг
                                          утгыг хоёр хяналтаар засвал
                                          цонх хаагдах хооронд аль нь
                                          хүчинтэйг хэрэглэгч таамаглана. */}
                                    <HzCard>
                                      <HzSectionTitle>
                                        Зөрчлийн төлөв
                                      </HzSectionTitle>
                                      <div
                                        style={{
                                          fontSize: 11.5,
                                          color: "#8A94A6",
                                        }}
                                      >
                                        Төлөвийг жагсаалтын «Төлөв»
                                        баганаас сольно
                                        {row.fix_status
                                          ? " — одоо: " +
                                            (FIX_META[row.fix_status] ||
                                              row.fix_status)
                                          : ""}
                                      </div>
                                      {row.fix_status === "closed" ? (
                                        <Input.TextArea
                                          rows={2}
                                          style={{ marginTop: 6 }}
                                          disabled={rowLocked(row)}
                                          placeholder="Хаалтын тайлбар — юу хийж арилгасан бэ"
                                          value={row.closed_note || ""}
                                          onChange={(e) =>
                                            setItem(row.id, {
                                              closed_note: e.target.value,
                                            })
                                          }
                                        />
                                      ) : null}
                                    </HzCard>
                                  </>
                                }
                              />
                            </Modal>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {items.length === 0 && (
        <Empty description="Энэ шатны маягтад үзүүлэлт алга (template хоосон эсвэл migration ажиллаагүй)" />
      )}

      {/* ── ХАРИУЦАГЧ: БҮХ НЭГЖЭЭС ─────────────────────────────────────
          Зөрчлийг арилгах хүн тухайн хэсгийнх байх АЛБАГҮЙ — цахилгааны
          зөрчлийг Цахилгаан цех, өргөх механизмынхыг ЗМЗ арилгана.
          ⚠ Хайлтаар БИШ ЖАГСААЛТААР: шалгагч өөр цехийн ажилтны нэрийг
            мэддэггүй («цахилгааны хэсгийн мастер» гэж л мэднэ) тул
            нэр бичүүлбэл тэр хүнийг ОГТ олохгүй. */}
      <Modal
        open={!!respPicker}
        title={
          <span style={{ fontSize: 14 }}>
            <i className="fas fa-sitemap" style={{ marginRight: 8, color: "#1D4E89" }} />
            Зөрчил арилгах хариуцагч — бүх нэгжээс
          </span>
        }
        onCancel={() => setRespPicker(null)}
        footer={null}
        width={620}
        destroyOnClose
      >
        <div style={{ fontSize: 12, color: "#8A94A6", marginBottom: 10 }}>
          Цех, хэсгээ сонгоход тэр нэгжийн ажилтнууд доор жагсана.
        </div>
        <Row gutter={10} style={{ marginBottom: 10 }}>
          <Col xs={24} sm={12}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#5F5E5A", marginBottom: 3 }}>
              Бүтцийн нэгж (цех)
            </div>
            <DepartmentTseh
              noAutoSelect
              value={pickTseh.code || undefined}
              onChange={(code, parentId, name) => {
                setPickTseh({ code: code || "", name: name || "", parentId });
                setPickSection({ code: "", name: "" });
              }}
            />
          </Col>
          <Col xs={24} sm={12}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#5F5E5A", marginBottom: 3 }}>
              Хэсэг (заавал биш)
            </div>
            <DepartmentNegj
              key={pickTseh.parentId || pickTseh.code || "no-tseh"}
              value={pickSection.code || undefined}
              paranetid={pickTseh.parentId}
              parentCode={pickTseh.code}
              autoSelectOwn={false}
              onChange={(code, name) =>
                setPickSection({ code: code || "", name: name || "" })
              }
            />
          </Col>
        </Row>

        <Spin spinning={pickLoading}>
          {!pickTseh.code ? (
            <div style={{ fontSize: 12, color: "#8A94A6", padding: "18px 0", textAlign: "center" }}>
              Эхлээд бүтцийн нэгжээ сонгоно уу
            </div>
          ) : !pickList.length && !pickLoading ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Энэ нэгжид идэвхтэй ажилтан олдсонгүй"
            />
          ) : (
            <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid #E6E9EF", borderRadius: 8 }}>
              {pickList.map((w) => (
                <button
                  key={w.tn}
                  type="button"
                  onClick={() => {
                    setItem(respPicker.rowId, {
                      responsible_tn: w.tn,
                      responsible_name: w.name || null,
                      // Дахин ачаалахад серверээс ирнэ; одоохондоо шууд
                      responsible_dept: (w.tseh_name || "").trim() || null,
                    });
                    setRespPicker(null);
                  }}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    border: "none",
                    borderBottom: "1px solid #F1F3F6",
                    background: "#fff",
                    padding: "7px 12px",
                    cursor: "pointer",
                    lineHeight: 1.3,
                  }}
                >
                  <span style={{ fontSize: 12.5, fontWeight: 600 }}>
                    {w.name || "?"}
                    <span style={{ color: "#8A94A6", fontWeight: 400 }}> · {w.tn}</span>
                  </span>
                  <div style={{ fontSize: 11, color: "#8A94A6" }}>
                    {(w.position || "—").trim()}
                    {w.tseh_name ? ` · ${w.tseh_name.trim()}` : ""}
                  </div>
                </button>
              ))}
            </div>
          )}
        </Spin>
      </Modal>

      <Divider />
      <Descriptions size="small" column={{ xs: 1, sm: 2 }} bordered>
        <Descriptions.Item label="Код">{insp.code}</Descriptions.Item>
        <Descriptions.Item label="Шат">
          {insp.stage_name} ({insp.stage_code})
        </Descriptions.Item>
        <Descriptions.Item label="Бүтцийн нэгж">
          {insp.tseh_name || "—"}
          {insp.bn_name ? ` · ${insp.bn_name}` : ""}
        </Descriptions.Item>
        <Descriptions.Item label="Ажлын байр">
          {insp.area_location || "—"}
        </Descriptions.Item>
        {/* ⚠ Байршил нь ЗААВАЛ БИШ (зөвшөөрөл өгөөгүй, байрны дотор
            сигналгүй байж болно) тул авсан үед Л харуулна — хоосон
            мөр нэмбэл «авах ёстой байсан» мэт төөрөгдүүлнэ.
            Газрын зураг дээр нээх холбоос: координат дангаараа
            хүнд юу ч хэлэхгүй. */}
        {insp.gps_lat && insp.gps_lng ? (
          <Descriptions.Item label="Байршил (GPS)">
            <a
              href={`https://www.google.com/maps?q=${insp.gps_lat},${insp.gps_lng}`}
              target="_blank"
              rel="noreferrer"
            >
              {Number(insp.gps_lat).toFixed(5)},{" "}
              {Number(insp.gps_lng).toFixed(5)}
            </a>
            {insp.gps_accuracy ? (
              <span style={{ fontSize: 11, color: "#8A94A6" }}>
                {" "}
                ±{Math.round(insp.gps_accuracy)} м
              </span>
            ) : null}
          </Descriptions.Item>
        ) : null}
        <Descriptions.Item label="Ажилтны тоо">
          {insp.worker_count ?? "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Огноо">
          {insp.inspection_date
            ? moment(insp.inspection_date).format("YYYY-MM-DD HH:mm")
            : "—"}
        </Descriptions.Item>
      </Descriptions>

      {/* ── ШАЛГАЛТЫГ ДАХИН НЭЭХ ─────────────────────────────────────
          ⚠ УДИРДЛАГАТАЙ Modal (`Modal.confirm` БИШ): шалтгаангүй үед
            `onOk`-оос алдаа шидэхэд antd түүнийг барьдаггүй тул
            консолд «Uncaught (in promise)» гарч, жинхэнэ алдаануудын
            дунд дуу чимээ болдог. Энд товч нь шалтгаан бичигдтэл
            ИДЭВХГҮЙ — буруу төлөвт дарах боломж үүсэхгүй. */}
      <Modal
        open={reopenOpen}
        title="Шалгалтыг дахин нээх үү?"
        width={520}
        okText="Дахин нээх"
        okButtonProps={{ danger: true, disabled: !reopenNote.trim() }}
        cancelText="Болих"
        confirmLoading={reopenBusy}
        onOk={submitReopen}
        onCancel={() => setReopenOpen(false)}
      >
        <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
          <p style={{ marginBottom: 8 }}>
            Шалгалт <b>нээлттэй</b> болж, зөрчилд даалгавар хуваарилах,
            биелэлт засах боломжтой болно. Хаалтын үед бодогдсон{" "}
            <b>үнэлгээ цэвэрлэгдэж</b>, дахин хаахад шинээр бодогдоно.
          </p>
          <Input.TextArea
            rows={3}
            value={reopenNote}
            onChange={(e) => setReopenNote(e.target.value)}
            placeholder="Буцаах шалтгаан (заавал) — аудитад үлдэнэ"
          />
        </div>
      </Modal>
    </div>
  );
}
